import classNames from 'classnames';
import { connect } from 'react-redux';
import { compose } from 'redux';
import {
    defineMessages,
    FormattedMessage,
    injectIntl,
    intlShape,
} from 'react-intl';
import PropTypes from 'prop-types';
import bindAll from 'lodash.bindall';
import bowser from 'bowser';
import React from 'react';

import VM from 'scratch-vm';
import ScratchBlocks from 'scratch-blocks';
import {
    parseVersion,
    WHO_CODE_TO_DEVICE_FAMILY,
    DEVICE_FAMILY_LABELS,
} from 'scratch-vm/src/util/device-version';

import Box from '../box/box.jsx';
import Button from '../button/button.jsx';
import DeviceRedirectModal from '../device-redirect-modal/device-redirect-modal.jsx';
import DeviceMismatchDialog from '../firmware-update-dialog/device-mismatch-dialog.jsx';
import FirmwareFlashDialog from '../firmware-update-dialog/firmware-flash-dialog.jsx';
import FirmwareUpdateDialog from '../firmware-update-dialog/firmware-update-dialog.jsx';
import {
    BootloaderConnectError,
    PortSelectionError,
    checkFirmwareVersion,
    flashFirmware,
} from '../../lib/firmware-update.js';
import CommunityButton from './community-button.jsx';
import ShareButton from './share-button.jsx';
import { ComingSoonTooltip } from '../coming-soon/coming-soon.jsx';
import Divider from '../divider/divider.jsx';
import LanguageSelector from '../../containers/language-selector.jsx';
import SaveStatus from './save-status.jsx';
import SBFileUploader from '../../containers/sb-file-uploader.jsx';
import ProjectWatcher from '../../containers/project-watcher.jsx';
import MenuBarMenu from './menu-bar-menu.jsx';
import { MenuItem, MenuSection } from '../menu/menu.jsx';
import ProjectTitleInput from './project-title-input.jsx';
import AuthorInfo from './author-info.jsx';
import AccountNav from '../../containers/account-nav.jsx';
import LoginDropdown from './login-dropdown.jsx';
import SB3Downloader from '../../containers/sb3-downloader.jsx';
import DeletionRestorer from '../../containers/deletion-restorer.jsx';
import TurboMode from '../../containers/turbo-mode.jsx';
import MenuBarHOC from '../../containers/menu-bar-hoc.jsx';
import inputImg from '../../../static/select bar@2x.png';
import { openTipsLibrary } from '../../reducers/modals';
import { setPlayer } from '../../reducers/mode';
import {
    autoUpdateProject,
    getIsUpdating,
    getIsShowingProject,
    manualUpdateProject,
    requestNewProject,
    remixProject,
    saveProjectAsCopy,
} from '../../reducers/project-state';
import {
    openAccountMenu,
    closeAccountMenu,
    accountMenuOpen,
    openFileMenu,
    closeFileMenu,
    fileMenuOpen,
    openEditMenu,
    closeEditMenu,
    editMenuOpen,
    openLanguageMenu,
    closeLanguageMenu,
    languageMenuOpen,
    openLoginMenu,
    closeLoginMenu,
    loginMenuOpen,
} from '../../reducers/menus';

import collectMetadata from '../../lib/collect-metadata';

import styles from './menu-bar.css';

import helpIcon from '../../lib/assets/icon--tutorials.svg';
import mystuffIcon from './icon--mystuff.png';
import profileIcon from './icon--profile.png';
import remixIcon from './icon--remix.svg';
import dropdownCaret from './dropdown-caret.svg';
import languageIcon from '../language-selector/language-icon.svg';
import aboutIcon from './icon--about.svg';

import scratchLogo from './back@3x.png';
import saveIcon from './save-secondary@3x.png';
import clearProgram from './resetBtn.png';
import uploadBtn from './uploadBtn.png';
import usbOff from './usb-off@3x.png';
import usbOn from './usb-on@3x.png';
import closeBtn from './close@3x.png';

import sharedMessages from '../../lib/shared-messages';
import { resetPortBusy, setPortBusy, setDeviceToast } from '../../reducers/device.js';
import { showMessageAlertWithTimeout } from '../../reducers/alerts';
import { setProjectTitle } from '../../reducers/project-title.js';
import { setCodes, setCurrentMode } from '../../reducers/script.js';

const ariaMessages = defineMessages({
    language: {
        id: 'gui.menuBar.LanguageSelector',
        defaultMessage: 'language selector',
        description: 'accessibility text for the language selection menu',
    },
    tutorials: {
        id: 'gui.menuBar.tutorialsLibrary',
        defaultMessage: 'Tutorials',
        description: 'accessibility text for the tutorials button',
    },
});

const MenuBarItemTooltip = ({
    children,
    className,
    enable,
    id,
    place = 'bottom',
}) => {
    if (enable) {
        return <React.Fragment>{children}</React.Fragment>;
    }
    return (
        <ComingSoonTooltip
            className={classNames(styles.comingSoon, className)}
            place={place}
            tooltipClassName={styles.comingSoonTooltip}
            tooltipId={id}
        >
            {children}
        </ComingSoonTooltip>
    );
};

MenuBarItemTooltip.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    enable: PropTypes.bool,
    id: PropTypes.string,
    place: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
};

const MenuItemTooltip = ({ id, isRtl, children, className }) => (
    <ComingSoonTooltip
        className={classNames(styles.comingSoon, className)}
        isRtl={isRtl}
        place={isRtl ? 'left' : 'right'}
        tooltipClassName={styles.comingSoonTooltip}
        tooltipId={id}
    >
        {children}
    </ComingSoonTooltip>
);

MenuItemTooltip.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    id: PropTypes.string,
    isRtl: PropTypes.bool,
};

const AboutButton = props => (
    <Button
        className={classNames(styles.menuBarItem, styles.hoverable)}
        iconClassName={styles.aboutIcon}
        iconSrc={aboutIcon}
        onClick={props.onClick}
    />
);

AboutButton.propTypes = {
    onClick: PropTypes.func.isRequired,
};
const toStoredDeviceCode = code => (code === 'PC' || code === 'Ace' ? 'Ace' : code);

// sensorByte 1 = 4-in-1 sensor (PeeCee Compute Kit 1), 2 = 2-in-1 sensor
// (PeeCee Compute Kit 2) — see getDeviceConfig()'s CONF reply handling.
const SENSOR_BYTE_TO_VARIANT_LABEL = {
    1: 'PeeCee Compute Kit 1',
    2: 'PeeCee Compute Kit 2',
};
class MenuBar extends React.Component {
    constructor(props) {
        super(props);
        this.sendDataRef = React.createRef();
        this.sendDataRef.current = {
            sendDataAsPacket: false,
            readVal: '',
            type: '',
        };

        const params = new URLSearchParams(window.location.search);
        this.device = params.get('device') || null;
        this.version = params.get('version') || null;

        const majorVersion = parseVersion(this.version).major;
        this.isAiEnable =
            (this.device === 'peeCee' && majorVersion === 1) ||
            (this.device === 'zing' && majorVersion === 2) ||
            this.device === 'poki';

        // AI lists are fetched lazily, only when the matching block/dropdown is
        // used. aiListDone dedupes per session; aiFetchQueue serialises them.
        this.aiListDone = {};
        this.aiFetchQueue = Promise.resolve();

        bindAll(this, [
            'handleClickNew',
            'handleClickRemix',
            'handleClickSave',
            'handleClickSaveAsCopy',
            'handleClickSeeCommunity',
            'handleClickShare',
            'handleKeyPress',
            'handleLanguageMouseUp',
            'handleRestoreOption',
            'getSaveToComputerHandler',
            'restoreOptionMessage',
            'handleClickUSB',
            'handleFocus',
            'handleBlur',
            'messageHandler',
            'confirmDeviceRedirect',
            'cancelDeviceRedirect',
            'confirmSensorVariantChange',
            'cancelSensorVariantChange',
            'handleFirmwareClose',
            'handleFirmwareSkip',
            'handleFirmwareUpdate',
        ]);
        this.state = {
            usbConnectionStatus: false,
            showSavePopup: false,
            tempProjectTitle: '',
            fileNameError: '',
            pendingDeviceRedirect: null,
            pendingSensorVariantChange: null,
            firmwareUpdate: null,
            firmwareFlash: null,
        };
        sessionStorage.setItem('usbConnectionStatus', false);
    }

    // Requested by the AI extension's menu getters (via the FETCH_AI_LIST
    // runtime event) only when a face/speech/audio block is actually shown.
    // Serialised so several dropdowns opening at once don't clash on the port.
    // When an AI extension is added/selected, probe whether the matching module
    // version actually answers its firmware-version command.
    handleCheckAiVersion = async protocol => {
        if (!this.isAiEnable) {
            return;
        }
        const label = protocol === 'v2' ? 'AI-1 v2' : 'AI-1 v1';
        if (sessionStorage.getItem('usbConnectionStatus') !== 'true') {
            this.props.setDeviceToast(
                'error',
                `Connect the device to detect ${label}`
            );
            return;
        }
        if (this.props.portBusy && this.props.portBusy.isPortBusy) {
            return;
        }
        const crc = this.props.vm.runtime.Bisoft.crc_create;
        let bytes;
        if (protocol === 'v2') {
            const payload = [0x03, 0xd3, 1]; // READ, FW_VERSION, read length
            const len = payload.length;
            bytes = [
                0xfa,
                0xf0,
                (len >> 8) & 0xff,
                len & 0xff,
                ...payload,
                crc([(len >> 8) & 0xff, len & 0xff, ...payload]),
                0xff,
                0xf9,
            ];
        } else {
            bytes = [0x41, 0x49, 2, 0x3f, crc([2, 0x3f]), 0xff, 0xf9];
        }

        this.props.setPortBusy({
            isPortBusy: true,
            portBusyFor: 'checkAiVersion',
            message: `Detecting ${label}...`,
        });

        const detected = await new Promise(resolve => {
            let settled = false;
            const finish = value => {
                if (settled) return;
                settled = true;
                this.props.worker.removeEventListener('message', onMsg);
                clearTimeout(timer);
                resolve(value);
            };
            const onMsg = e => {
                if (e.data && e.data.type === 'readAnyFormat') {
                    finish(true);
                }
            };
            this.props.worker.addEventListener('message', onMsg);
            const timer = setTimeout(() => finish(false), 3000);
            this.props.worker.postMessage({
                type: 'WriteArrayAiTrainFetch',
                value: bytes,
            });
        });

        this.props.resetPortBusy();
        if (detected) {
            this.props.setDeviceToast('success', `${label} connected`);
        } else {
            this.props.setDeviceToast(
                'error',
                `${label} not detected — check the module connection`
            );
        }
    }

    handleFetchAiList = (which, protocol = 'v1') => {
        this.aiFetchQueue = this.aiFetchQueue
            .then(() => this.runAiListFetch(which, protocol))
            .catch(e => /* eslint-disable */console.log(...oo_oo(`4052364469_334_24_334_47_4`,'ERROR', e)));
        return this.aiFetchQueue;
    }

    // v1 lists ride the A/I protocol; v2 (AI-0) lists ride the 0xFA protocol and
    // have no audio/MP3 list (v2 uses text-to-speech instead).
    aiListConfig = {
        v1: {
            audio: {
                cmd: 0x1c,
                portBusyFor: 'fetchAudioList',
                message: 'Fetching the audio list, please wait...',
                waitTime: 7000,
            },
            face: {
                cmd: 0x1f,
                portBusyFor: 'fetchFaceList',
                message: 'Fetching the face list, please wait...',
                waitTime: 12000,
            },
            speech: {
                cmd: 0x15,
                portBusyFor: 'fetchSpeechList',
                message: 'Fetching the speech list, please wait...',
                waitTime: 7000,
            },
        },
        v2: {
            face: {
                cmd: 0x33,
                portBusyFor: 'fetchFaceList',
                message: 'Fetching the face list, please wait...',
                waitTime: 3000,
            },
            speech: {
                cmd: 0x83,
                portBusyFor: 'fetchSpeechList',
                message: 'Fetching the speech list, please wait...',
                waitTime: 3000,
            },
        },
    }

    buildAiFetchFrame(protocol, cmd) {
        const crc = this.props.vm.runtime.Bisoft.crc_create;
        if (protocol === 'v2') {
            // 0xFA 0xF0 LEN_MSB LEN_LSB TYPE(READ=0x03) CMD <read-len> CRC 0xFF 0xF9
            const payload = [0x03, cmd, 0];
            const len = payload.length;
            return [
                0xfa,
                0xf0,
                (len >> 8) & 0xff,
                len & 0xff,
                ...payload,
                crc([(len >> 8) & 0xff, len & 0xff, ...payload]),
                0xff,
                0xf9,
            ];
        }
        // v1: A I LEN CMD CRC 0xFF 0xF9
        return [0x41, 0x49, 2, cmd, crc([2, cmd]), 0xff, 0xf9];
    }

    runAiListFetch = async(which, protocol) => {
        const perProtocol = this.aiListConfig[protocol];
        const cfg = perProtocol && perProtocol[which];
        if (!cfg || !this.isAiEnable) {
            return;
        }
        if (sessionStorage.getItem('usbConnectionStatus') !== 'true') {
            return;
        }
        const doneKey = `${protocol}:${which}`;
        if (this.aiListDone[doneKey]) {
            return;
        }
        // Another port operation (e.g. a program upload) is running; leave the
        // list unfetched so the next dropdown open retries.
        if (this.props.portBusy && this.props.portBusy.isPortBusy) {
            return;
        }
        await this.fetchLogic({
            bytes: this.buildAiFetchFrame(protocol, cfg.cmd),
            portBusyFor: cfg.portBusyFor,
            message: cfg.message,
            waitTime: cfg.waitTime,
            protocol,
            attempts: protocol === 'v2' ? 5 : 2,
        });
        this.aiListDone[doneKey] = true;
        try {
            await this.props.vm.extensionManager.refreshBlocks();
        } catch (e) {
            /* eslint-disable */console.log(...oo_oo(`4052364469_428_12_428_73_4`,'ERROR refreshing blocks after AI list fetch', e));
        }
    }
    componentDidMount() {
        // check the scratch window is onfocus or not
        window.addEventListener('focus', this.handleFocus);
        window.addEventListener('blur', this.handleBlur);

        document.addEventListener('keydown', this.handleKeyPress);
        this.props.worker.addEventListener('message', this.messageHandler);
        this.props.vm.runtime.on('FETCH_AI_LIST', this.handleFetchAiList);
        this.props.vm.runtime.on('CHECK_AI_VERSION', this.handleCheckAiVersion);
        // if (window.location.search === '?pc1.0') {
        //     document.addEventListener('DOMContentLoaded', function () {
        //         navigator.usb.getDevices().then((devices) => {
        //             return Connect(devices[0])
        //         })
        //     })
        //     navigator.usb.addEventListener('connect', (e) => {
        //         let port = e.device
        //         Connect(port)
        //     })
        //     navigator.usb.addEventListener('disconnect', (e) => {
        //         this.setState({
        //             usbConnectionStatus: false,
        //         })
        //     })
        // } else {
        // DOMContentLoaded fires before React mounts, so that listener never runs.
        // Instead, try to auto-connect shortly after mount using previously-granted ports.
        // After a device redirect-and-reload the port is always in getPorts() so we
        // use a shorter delay to reconnect sooner and clear the flag immediately.
        const redirectDelay = sessionStorage.getItem('pendingSerialReconnect') === '1' ? 300 : 800;
        sessionStorage.removeItem('pendingSerialReconnect');
        setTimeout(async () => {
            try {
                const ports = await navigator.serial.getPorts();
                if (ports && ports.length > 0) Connect(ports[0]);
            } catch (_) {}
        }, redirectDelay);
        navigator.serial.addEventListener('connect', e => {
            // Always auto-connect when a USB device is plugged in — the
            // isWindowOnFocus guard was causing missed connections when the
            // user switched tabs before plugging in.
            Connect(e.target);
        });
        // After a C-mode upload the device reboots. controls.jsx dispatches this
        // so we reconnect the worker once the device is back up and set USB green.
        window.addEventListener('scratch:reconnect-serial', async () => {
            await new Promise(r => setTimeout(r, 2000));
            try {
                const ports = await navigator.serial.getPorts();
                if (ports && ports.length > 0) Connect(ports[0]);
            } catch (_) {}
        });
        navigator.serial.addEventListener('disconnect', e => {
            // console.log(e.target)
            // if (e.target) {
            //     e.target.close()
            // }
            this.props.worker.postMessage({ type: 'disconnected' });
            this.setState({
                usbConnectionStatus: false,
            });
            sessionStorage.setItem('usbConnectionStatus', false);
            // Keep connectedDevice/deviceVersion/deviceMajor/deviceMinor so the
            // app still knows the last-connected device (e.g. when the user
            // navigates back after disconnecting). They are overwritten when a
            // new device connects.
            // sessionStorage.removeItem('deviceVersion');
            // sessionStorage.removeItem('connectedDevice');
            // sessionStorage.removeItem('deviceMajor');
            // sessionStorage.removeItem('deviceMinor');
            // Bug #1: do NOT clear the sensor variant or flip hasFourInOne/
            // hasTwoInOne to false on disconnect. Doing so refreshed the palette
            // with the sensor block gone, which orphaned any sensor block already
            // placed in the workspace (e.g. inside an If) — it vanished. The block
            // must remain; the last-known sensor variant is kept so it stays valid,
            // and reconnecting the same sensor simply resumes. The variant is only
            // updated when a new CONF reports a different sensor.
            window.dispatchEvent(new Event('sensorVariantUpdated'));
        });

        const Connect = async port => {
            // The flasher owns the serial port while an update is running.
            if (sessionStorage.getItem('firmwareUpdateInProgress') === 'true') {
                return;
            }
            try {
                const getConfirmation = async () => {
                    return new Promise((resolve, reject) => {
                        this.props.worker.postMessage({
                            type: 'connected',
                        });
                        const tempHandler = (e) => {
                            if (e.data.type == 'connected' && e.data.value) {
                                this.props.worker.removeEventListener('message', tempHandler);
                                // Mirror plode-scripting: check VID/PID from worker info
                                // WCH CH592 (Toki/Poki): VID=0x1a86, PID=0x8040
                                // Skip WHO for these — Serial is UART not USB CDC
                                const vid = Number(e.data.info?.usbVendorId);
                                const pid = Number(e.data.info?.usbProductId);
                                /* eslint-disable */console.log(...oo_oo(`4052364469_530_32_530_123_4`,`[autoDetect] connected — vid=0x${vid.toString(16)} pid=0x${pid.toString(16)}`));
                                if (vid === 0x1a86 && pid === 0x8040) {
                                    /* eslint-disable */console.log(...oo_oo(`4052364469_532_36_532_113_4`,'[autoDetect] WCH CH592 detected via VID/PID — staging redirect'));
                                    resolve({ isWch: true });
                                } else {
                                    resolve({ isWch: false });
                                }
                            }
                            if (e.data.type == 'connected' && !e.data.value) {
                                this.props.worker.removeEventListener('message', tempHandler);
                                resolve(null);
                            }
                        };
                        this.props.worker.addEventListener('message', tempHandler);
                    });
                };
                const confirm = await getConfirmation();
                if (!confirm) {
                    // Connection failed
                } else if (confirm.isWch) {
                    // WCH CH592 (Toki/Poki): Serial = UART, USB is a separate interface.
                    // WHO bytes go over USB but the device reads them on UART → no response.
                    // Can't distinguish Toki from Poki by VID/PID alone.
                    // Strategy: keep the URL device if it's already a WCH device (toki/poki).
                    // If URL is a non-WCH device (e.g. peeCee), redirect to toki as default
                    // and let the user correct it if needed.
                    sessionStorage.setItem('usbConnectionStatus', true);
                    this.setState({ usbConnectionStatus: true });
                    const _urlDevice = new URLSearchParams(window.location.search).get('device') || '';
                    const _urlVersion = new URLSearchParams(window.location.search).get('version') || '1.0.2';
                    const _wchDevices = ['toki', 'poki'];
                    if (_wchDevices.includes(_urlDevice.toLowerCase())) {
                        // Already on the right WCH device URL — nothing to redirect
                        /* eslint-disable */console.log(...oo_oo(`4052364469_563_24_563_111_4`,'[autoDetect] WCH device, URL already set to', _urlDevice, '— no redirect'));
                    } else {
                        // URL is for a non-WCH device — redirect to toki as default
                        // (user can manually switch to poki if needed)
                        /* eslint-disable */console.log(...oo_oo(`4052364469_567_24_567_99_4`,'[autoDetect] WCH device on non-WCH URL — redirecting to toki'));
                        this.redirectToConnectedDevice('TK', _urlVersion);
                    }
                } else if (confirm && !confirm.isWch) {
                    sessionStorage.setItem('usbConnectionStatus', true);
                    this.setState({
                        usbConnectionStatus: true,
                    });

                    // Small delay so the device is fully ready before WHO probe
                    await new Promise(r => setTimeout(r, 500));
                    await this.getDeviceConfig();
                    if (!this.props.currentMode.isMicroPythonMode) {
                        await this.getCustomAction();
                        this.aiListDone = {};
                    }
                }
            } catch (e) {
                /* eslint-disable */console.log(...oo_oo(`4052364469_585_16_585_39_4`,'ERROR', e));
            }
            // }
        };
    }

    componentWillUnmount() {
        document.removeEventListener('keydown', this.handleKeyPress);
        this.props.vm.runtime.off('FETCH_AI_LIST', this.handleFetchAiList);
        this.props.vm.runtime.off('CHECK_AI_VERSION', this.handleCheckAiVersion);
        // if (window.location.search === '?pc1.0') {
        //     document.removeEventListener('DOMContentLoaded', function () {
        //         navigator.usb.getDevices().then((devices) => {
        //             return Connect(devices[0])
        //         })
        //     })
        //     navigator.usb.removeEventListener('connect', (e) => {
        //         this.setState({
        //             usbConnectionStatus: true,
        //         })
        //     })
        //     navigator.usb.removeEventListener('disconnect', (e) => {
        //         const port = e.device
        //         this.setState({
        //             usbConnectionStatus: false,
        //         })
        //         port.releaseInterface(1)
        //     })
        // }
        // const Connect = async (port) => {
        //     try {
        //         let configNumber_ = 1
        //         let interfaceNumber_ = 1
        //         await port.open()
        //         if (port.configuration === null)
        //             await port.selectConfiguration(configNumber_)
        //         await port.claimInterface(interfaceNumber_)
        //         if (port.opened === true) {
        //             sessionStorage.setItem('usbConnectionStatus', true)
        //             this.setState({
        //                 usbConnectionStatus: true,
        //             })
        //         }
        //     } catch (e) {
        //         console.log(e)
        //     }
        // }
        window.removeEventListener('focus', this.handleFocus);
        window.removeEventListener('blur', this.handleBlur);
        this.props.worker.removeEventListener('message', this.messageHandler);
    }

    handleFocus() {
        /* eslint-disable */console.log(...oo_oo(`4052364469_638_8_638_35_4`,'Window focus'));
        sessionStorage.setItem('isWindowOnFocus', true);
    }

    handleBlur() {
        /* eslint-disable */console.log(...oo_oo(`4052364469_643_8_643_42_4`,'window out of focus'));
        sessionStorage.setItem('isWindowOnFocus', false);
    }
    async handleClickUSB() {
        if (sessionStorage.getItem('usbConnectionStatus') === 'true') {
            return;
        }
        // if (window.location.search === '?pc1.0') {
        //     if (!'usb' in navigator) {
        //         window.alert('Web USB API is not supported on this browser')
        //         return
        //     }
        //     const filters = [
        //         { vendorId: 0xb1b0, productId: 0x93c3 },
        //         { vendorId: 0x303a, productId: 0x1001 },
        //     ]
        //     navigator.usb
        //         .requestDevice({
        //             filters,
        //         })
        //         .then(async (port) => {
        //             try {
        //                 let configNumber_ = 1
        //                 let interfaceNumber_ = 1
        //                 await port.open()

        //                 if (port.configuration === null)
        //                     await port.selectConfiguration(configNumber_)
        //                 await port.claimInterface(interfaceNumber_)
        //                 this.setState({
        //                     usbConnectionStatus: true,
        //                 })
        //                 sessionStorage.setItem('usbConnectionStatus', true)
        //             } catch (e) {}
        //         })
        // } else {
        let port;
        if ('serial' in navigator) {
            /* eslint-disable */console.log(...oo_oo(`4052364469_681_12_681_46_4`,'webSerial supported'));
            const filters = [
                { usbVendorId: 0x1a86, usbProductId: 0x7523 },
                { usbVendorId: 0x303a, usbProductId: 0x1001 },
                { usbVendorId: 0x1a86, usbProductId: 0x8040 },
                { usbVendorId: 0xb1b0, usbProductId: 0x8055 },
                { usbVendorId: 0x303A },
            ];
            // navigator.serial
            //     .requestPort({ filters })
            //     .then(async (selectedport) => {
            //         port = selectedport
            //         try {
            //             await port.open({ baudRate: 120000 })
            //         } catch (e) {}
            //         this.setState({
            //             usbConnectionStatus: true,
            //         })
            //         sessionStorage.setItem('usbConnectionStatus', true)
            //         if (
            //             [
            //                 '?zing1.0',
            //                 '?zing1.0s3',
            //                 '?hexapod',
            //                 '?hexapod1.0s3',
            //             ].includes(window.location.search)
            //         ) {
            //             this.props.vm.fetchCustomAction(port)
            //         }
            //         // this.props.vm.greenFlag()
            //     })
            port = await navigator.serial.requestPort({ filters });
            const getConfirmation = async() => new Promise((resolve, reject) => {
                this.props.worker.postMessage({
                    type: 'connected',
                });
                const tempHandler = e => {
                    if (e.data.type == 'connected' && e.data.value) {
                        this.props.worker.removeEventListener('message', tempHandler);
                        resolve(true);
                    }
                    if (e.data.type == 'connected' && !e.data.value) {
                        this.props.worker.removeEventListener('message', tempHandler);
                        resolve(false);
                    }
                };
                this.props.worker.addEventListener('message', tempHandler);
            });
            const confirm = await getConfirmation();
            if (confirm) {
                this.setState({
                    usbConnectionStatus: true,
                });
                sessionStorage.setItem('usbConnectionStatus', true);

                await this.getDeviceConfig();
                if (!this.props.currentMode.isMicroPythonMode) {
                    await this.getCustomAction();
                    this.aiListDone = {};
                }
            }
        } else {
            window.alert('Web Serial API is not supported on this browser');
        }
        // }
    }

    async fetchLogic({
        bytes,
        portBusyFor,
        message,
        waitTime = 2000,
        protocol = 'v1',
        attempts = 2,
    }) {
        if (
            // !['?pc1.0', '?zing1.0s3', '?zing3.0'].includes(
            //     window.location.search
            // )
            !this.isAiEnable
        ) {
            return;
        }

        this.props.setPortBusy({
            isPortBusy: true,
            portBusyFor,
            message,
        });

        await new Promise(reslove => setTimeout(reslove, 500));

        const bisoft = this.props.vm.runtime.Bisoft;
        for (let i = 0; i < attempts; i++) {
            if (sessionStorage.getItem('usbConnectionStatus') == 'false') {
                break;
            }
            this.props.worker.postMessage({
                type: 'WriteArrayAiTrainFetch',
                value: bytes,
            });
            this.sendDataRef.current = {
                sendDataAsPacket: false,
                readVal: '',
                type: portBusyFor,
            };
            let replyData = '';
            try {
                replyData = await Promise.race([
                    this.checkOk(),
                    this.timeoutPromise(waitTime), //
                ]);
            } catch (e) {
                /* eslint-disable */console.log(...oo_oo(`4052364469_794_16_794_39_4`,'ERROR', e));
            }

            if (replyData.type === portBusyFor) {
                /* eslint-disable */console.log(
                    ...oo_oo(`4052364469_798_16_806_17_4`,`[AI-FETCH] ${protocol} ${portBusyFor} — ${(replyData.readVal || []).length
                    } item(s):`,
                    (replyData.readVal || []).map(item =>
                        (item && typeof item === 'object' ?
                            `${item.id}:${item.label}` :
                            item)
                    ))
                );
                if (replyData.type === 'fetchAudioList') {
                    bisoft.audioList = replyData.readVal;
                    break;
                }
                if (replyData.type === 'fetchFaceList') {
                    if (protocol === 'v2') bisoft.faceListV2 = replyData.readVal;
                    else bisoft.faceList = replyData.readVal;
                    break;
                }
                if (replyData.type === 'fetchSpeechList') {
                    if (protocol === 'v2') {
                        bisoft.speechListV2 = replyData.readVal;
                    } else {
                        bisoft.speechList = replyData.readVal;
                    }
                    break;
                }
            }
        }
        this.props.resetPortBusy();
    }

    async getCustomAction() {
        if (
            // [
            //     '?zing1.0',
            //     '?zing1.0s3',
            //     '?hexapod',
            //     '?hexapod1.0s3',
            //     '?zing3.0',
            // ].includes(window.location.search)
            ['zing', 'crawle'].includes(this.device) &&
            [1, 2, 3].includes(parseVersion(this.version).major)
        ) {
            this.props.setPortBusy({
                isPortBusy: true,
                portBusyFor: 'fetchCustomAction',
                message: 'Fetching the custom action please wait....',
            });
            await new Promise(reslove => setTimeout(reslove, 500));
            for (let i = 0; i < 7; i++) {
                this.props.worker.postMessage({
                    type: 'WriteArrayFormat1310',
                    value: ['F'.charCodeAt(0), 'R'.charCodeAt(0)],
                });
                this.sendDataRef.current = {
                    sendDataAsPacket: false,
                    readVal: '',
                    type: 'fetchCustomAction',
                };
                let replyData = '';
                try {
                    replyData = await Promise.race([
                        this.checkOk(),
                        this.timeoutPromise(2000), //
                    ]);
                } catch (e) {
                    /* eslint-disable */console.log(...oo_oo(`4052364469_864_20_864_43_4`,'ERROR', e));
                }

                if (replyData.type === 'fetchCustomAction') {
                    await this.props.vm.fetchCustomAction(replyData.readVal);
                    break;
                }
            }
            this.props.resetPortBusy();
        }
    }

    // Adapt sensor blocks (input_fourinone — a single block whose label + port
    // menu switch between the 4-in-1 and 2-in-1 variants) that are already placed
    // in the workspace to the currently-connected sensor. Bug #1: this used to
    // DISPOSE the blocks (deleting them from the workspace / leaving an empty If).
    // Now we KEEP the block and only fix a PORT value that is invalid for the new
    // variant. The 2-in-1 supports only 'Proximity' and 'Light'; the 4-in-1
    // supports all ports, so nothing needs fixing when switching to 4-in-1.
    adaptSensorBlocksToVariant(hasTwoInOne, variantChanged) {
        /* eslint-disable */console.log(...oo_oo(`4052364469_884_8_884_128_4`,'[CONF] adaptSensorBlocksToVariant called | hasTwoInOne:', hasTwoInOne, '| variantChanged:', variantChanged));
        const workspace = ScratchBlocks.getMainWorkspace();
        if (!workspace) {
            console.warn('[CONF] adaptSensorBlocksToVariant — no main workspace found');
            return;
        }
        const twoInOnePorts = ['Proximity', 'Light'];
        const sensorBlocks = workspace
            .getAllBlocks(false)
            .filter(block => block.type && block.type.includes('input_fourinone'));
        sensorBlocks.forEach(block => {
            if (hasTwoInOne) {
                const port = block.getFieldValue('PORT');
                if (!twoInOnePorts.includes(port)) {
                    /* eslint-disable */console.log(...oo_oo(`4052364469_898_20_898_98_4`,'[CONF] adapting sensor block PORT', port, '→ Proximity (2-in-1)'));
                    block.setFieldValue('Proximity', 'PORT');
                }
            }
            // 4-in-1: all ports valid — keep the block and its PORT as-is.
        });
        if (typeof workspace.render === 'function') {
            workspace.render();
        }
        // The sensor block's LABEL ("Four in one" vs "Two in one sensor reads")
        // is fixed at creation, so setFieldValue above updates the PORT but not
        // the label of an already-placed block. When the variant actually changed
        // and such blocks exist, ask the VM to re-emit the workspace so the blocks
        // are rebuilt from the (authoritative) VM model — this re-runs each block's
        // definition and refreshes the dynamic label, while staying VM-synced so
        // code generation keeps working. Deferred a tick so the PORT field change
        // above flushes to the VM before it re-emits.
        if (variantChanged && sensorBlocks.length > 0 &&
            typeof this.props.vm.refreshWorkspace === 'function') {
            setTimeout(() => {
                /* eslint-disable */console.log(...oo_oo(`4052364469_918_16_918_120_4`,'[CONF] refreshWorkspace() — rebuilding placed blocks so labels match the connected sensor'));
                this.props.vm.refreshWorkspace();
            }, 0);
        }
    }
    // Instead of redirecting immediately, this stages the redirect and shows
    // a confirmation dialog (DeviceRedirectModal) — the actual URL rewrite +
    // reload happens in confirmDeviceRedirect() once the user agrees.
    redirectToConnectedDevice(whoCode, deviceVersion) {
        // Debounce: don't fire within 5s of the last redirect to avoid bounce
        // when multiple devices are connected (e.g. PeeCee + Toki both trigger).
        const now = Date.now();
        if (this._lastRedirectTime && now - this._lastRedirectTime < 5000) {
            /* eslint-disable */console.log(...oo_oo(`4052364469_931_12_931_89_4`,'[autoDetect] redirect debounced — too soon after last redirect'));
            return false;
        }

        const family = WHO_CODE_TO_DEVICE_FAMILY[whoCode];
        if (!family) {
            console.warn(
                '[WHO] no known device= family for WHO code:',
                whoCode,
                '— leaving URL unchanged'
            );
            return false;
        }

        const currentParams = new URLSearchParams(window.location.search);
        const currentDevice = currentParams.get('device');
        const currentVersion = currentParams.get('version');
        if (currentDevice === family && currentVersion === deviceVersion) {
            // URL already matches the connected device — nothing to do
            return false;
        }

        /* eslint-disable */console.log(
            ...oo_oo(`4052364469_953_8_961_9_4`,'[WHO] connected device differs from the URL — asking user to confirm redirect:',
            family,
            deviceVersion,
            '(was',
            currentDevice,
            currentVersion,
            ')')
        );
        this.setState({
            pendingDeviceRedirect: {
                currentDevice,
                currentVersion,
                targetFamily: family,
                targetVersion: deviceVersion,
            },
        });
        this._lastRedirectTime = Date.now();
        return true;
    }

    confirmDeviceRedirect() {
        const pending = this.state.pendingDeviceRedirect;
        if (!pending) return;
        /* eslint-disable */console.log(
            ...oo_oo(`4052364469_977_8_981_9_4`,'[WHO] user confirmed device redirect — reloading as',
            pending.targetFamily,
            pending.targetVersion)
        );
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.set('device', pending.targetFamily);
        currentParams.set('version', pending.targetVersion);
        const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
        window.history.replaceState({}, '', newUrl);
        // Flag: the componentDidMount auto-connect timer uses a shorter delay
        // (300 ms instead of 800 ms) so the icon goes green faster after reload.
        sessionStorage.setItem('pendingSerialReconnect', '1');
        // The user already confirmed via our own dialog — suppress the
        // browser's native "leave page?" prompt (set by project-saver-hoc.jsx
        // to warn about unsaved project changes) for this one reload.
        window.onbeforeunload = null;
        location.reload();
    }

    cancelDeviceRedirect() {
        /* eslint-disable */console.log(
            ...oo_oo(`4052364469_998_8_1000_9_4`,'[WHO] user cancelled device redirect — staying on current device (forcing disconnect)')
        );
        this.setState({
            pendingDeviceRedirect: null,
            usbConnectionStatus: false
        });
        sessionStorage.setItem('usbConnectionStatus', false);
        if (this.props.worker) {
            this.props.worker.postMessage({ type: 'disconnected' });
        }
    }

    // Actually applies a detected PeeCee sensor variant: flips the VM flags,
    // persists it, refreshes the block palette and adapts any already-placed
    // sensor blocks. Called directly for the very first CONF reply in a
    // session (nothing to mismatch against yet), and from
    // confirmSensorVariantChange() once the user agrees to a later change.
    async applySensorVariant(sensorByte, hasFourInOne, hasTwoInOne, variantChanged) {
        this.props.vm.runtime.Bisoft.hasFourInOne = hasFourInOne;
        this.props.vm.runtime.Bisoft.hasTwoInOne = hasTwoInOne;
        sessionStorage.setItem('sensorVariant', sensorByte);
        // sessionStorage is per-origin and plode-web runs on a different
        // port/origin, so it can't read 'sensorVariant' directly. A cookie
        // (host-scoped but port-agnostic) is the one thing that survives the
        // hard top-level navigation back to plode-web's "back" button —
        // plode-web reads and clears this on load to pick up the variant the
        // user just confirmed here, instead of showing whatever was selected
        // before entering the editor.
        document.cookie = `peeCeeVariantSync=${sensorByte}; path=/; max-age=120`;
        /* eslint-disable */console.log(...oo_oo(`4052364469_1028_8_1028_88_4`,'[CONF] hasFourInOne:', hasFourInOne, '| hasTwoInOne:', hasTwoInOne));
        /* eslint-disable */console.log(...oo_oo(`4052364469_1029_8_1029_97_4`,'[CONF] calling extensionManager.refreshBlocks() to update block palette...'));
        try {
            await this.props.vm.extensionManager.refreshBlocks();
            /* eslint-disable */console.log(...oo_oo(`4052364469_1032_12_1032_72_4`,'[CONF] refreshBlocks() done — palette updated'));
        } catch (e) {
            /* eslint-disable */console.error(...oo_tx(`4052364469_1034_12_1034_62_11`,'[CONF] refreshBlocks() failed:', e));
        }
        this.adaptSensorBlocksToVariant(hasTwoInOne, variantChanged);
        window.dispatchEvent(new Event('sensorVariantUpdated'));
    }

    confirmSensorVariantChange() {
        const pending = this.state.pendingSensorVariantChange;
        if (!pending) return;
        /* eslint-disable */console.log(
            ...oo_oo(`4052364469_1043_8_1046_9_4`,'[CONF] user confirmed PeeCee sensor variant change — switching to',
            SENSOR_BYTE_TO_VARIANT_LABEL[pending.sensorByte] || pending.sensorByte)
        );
        this.setState({ pendingSensorVariantChange: null });
        this.applySensorVariant(pending.sensorByte, pending.hasFourInOne, pending.hasTwoInOne, true)
            .then(() => this.checkFirmwareAfterConnect());
    }

    cancelSensorVariantChange() {
        /* eslint-disable */console.log(
            ...oo_oo(`4052364469_1053_8_1055_9_4`,'[CONF] user cancelled PeeCee sensor variant change — disconnecting; the mismatch is re-checked on reconnect')
        );
        // sessionStorage.sensorVariant is deliberately left at the previously
        // confirmed value, so reconnecting the same mismatched device
        // re-detects the mismatch and shows this dialog again.
        this.setState({
            pendingSensorVariantChange: null,
            usbConnectionStatus: false,
        });
        sessionStorage.setItem('usbConnectionStatus', false);
        if (this.props.worker) {
            this.props.worker.postMessage({ type: 'disconnected' });
        }
    }

    async getDeviceConfig() {
        /* eslint-disable */console.log(...oo_oo(`4052364469_1070_8_1070_69_4`,'[getDeviceConfig] starting WHO + CONF sequence'));
        this.props.setPortBusy({
            isPortBusy: true,
            portBusyFor: 'getDeviceConfig',
            message: 'Detecting device, please wait....',
        });
        try {
            await this._getDeviceConfig();
        } finally {
            this.props.resetPortBusy();
        }
        // Device-mismatch dialogs come first; the firmware check only runs
        // once the connected device has been accepted.
        await this.checkFirmwareAfterConnect();
    }

    disconnectDevice() {
        this.setState({usbConnectionStatus: false});
        sessionStorage.setItem('usbConnectionStatus', false);
        if (this.props.worker) {
            this.props.worker.postMessage({type: 'disconnected'});
        }
    }

    async checkFirmwareAfterConnect() {
        if (this.state.pendingDeviceRedirect || this.state.pendingSensorVariantChange) {
            return;
        }
        if (this.props.currentMode && this.props.currentMode.isMicroPythonMode) {
            return;
        }
        if (sessionStorage.getItem('usbConnectionStatus') !== 'true') {
            return;
        }
        const whoCode = sessionStorage.getItem('deviceWhoCode');
        const version = sessionStorage.getItem('deviceVersion');
        if (!whoCode || !version) {
            return;
        }
        try {
            const check = await checkFirmwareVersion(whoCode, version);
            const ignored =
                sessionStorage.getItem('ignoredFirmwareKey') === `${whoCode}:${version}`;
            if (
                check.updateAvailable &&
                (check.mandatory || !ignored) &&
                sessionStorage.getItem('usbConnectionStatus') === 'true'
            ) {
                this.setState({firmwareUpdate: check});
            }
        } catch (e) {
            /* eslint-disable */console.error(...oo_tx(`4052364469_1121_12_1121_56_11`,'[firmware] check failed:', e));
        }
    }

    // Closing the dialog disconnects the device (also for optional updates).
    handleFirmwareClose() {
        this.setState({firmwareUpdate: null});
        this.disconnectDevice();
    }

    handleFirmwareSkip() {
        const check = this.state.firmwareUpdate;
        if (check) {
            sessionStorage.setItem(
                'ignoredFirmwareKey',
                `${check.whoCode}:${check.currentVersion}`
            );
        }
        this.setState({firmwareUpdate: null});
    }

    handleFirmwareUpdate() {
        const check = this.state.firmwareUpdate;
        this.setState({firmwareUpdate: null});
        if (check) {
            this.runFirmwareFlash(check);
        }
    }

    releaseWorkerPort() {
        return new Promise(resolve => {
            const worker = this.props.worker;
            if (!worker) {
                resolve();
                return;
            }
            let timer = null;
            const handler = e => {
                if (e.data && e.data.type === 'connected' && !e.data.value) {
                    clearTimeout(timer);
                    worker.removeEventListener('message', handler);
                    resolve();
                }
            };
            timer = setTimeout(() => {
                worker.removeEventListener('message', handler);
                resolve();
            }, 5000);
            worker.addEventListener('message', handler);
            this.disconnectDevice();
        });
    }

    async runFirmwareFlash(check) {
        sessionStorage.setItem('firmwareUpdateInProgress', 'true');
        const finish = () => {
            sessionStorage.removeItem('firmwareUpdateInProgress');
            this.setState({firmwareFlash: null});
        };
        const showResult = (title, text, buttons) =>
            this.setState({firmwareFlash: {title, text, buttons}});
        const retryButtons = () => [
            {text: 'Try Again', onClick: () => this.runFirmwareFlash(check)},
            {text: 'Cancel', onClick: finish},
        ];

        this.setState({firmwareFlash: {text: 'Preparing device...', buttons: []}});
        // Release the serial port from the worker so the flasher can use it.
        // Wait for the worker to confirm it closed the port (it can take
        // well over 500ms to cancel the reader), like plode-web does.
        await this.releaseWorkerPort();
        await new Promise(resolve => setTimeout(resolve, 500));

        try {
            await flashFirmware(check, (text, progress) =>
                this.setState({firmwareFlash: {text, progress, buttons: []}})
            );
            sessionStorage.removeItem('versionData');
            sessionStorage.removeItem('ignoredFirmwareKey');
            showResult(
                'Firmware Updated',
                'Firmware updated successfully!\n\nPlease disconnect and reconnect the device.',
                [{text: 'OK', onClick: finish}]
            );
        } catch (e) {
            /* eslint-disable */console.error(...oo_tx(`4052364469_1206_12_1206_57_11`,'[firmware] update failed:', e));
            if (e instanceof PortSelectionError) {
                showResult(
                    'Firmware Update',
                    'Device selection was cancelled.\n\nWould you like to try again?',
                    retryButtons()
                );
            } else if (e instanceof BootloaderConnectError) {
                showResult(
                    'Firmware Update',
                    'Could not connect to bootloader.\n\nPlease try again:\n1. Disconnect the device\n2. Hold the BOOT button\n3. Reconnect via USB\n4. Release the BOOT button\n5. Click "Try Again"',
                    retryButtons()
                );
            } else {
                showResult(
                    'Firmware Update Failed',
                    `Update failed: ${e.message}`,
                    retryButtons()
                );
            }
        }
    }

    // Compares the connected PeeCee sensor variant with the one selected in
    // plode-web. A change is staged for the "Device mismatch found" dialog;
    // otherwise it is applied right away.
    async handleSensorVariant(sensorByte) {
        const hasFourInOne = sensorByte === 1;
        const hasTwoInOne = sensorByte === 2;
        // Detect a sensor-variant CHANGE (e.g. swapping a 4-in-1 PeeCee for
        // a 2-in-1, or vice-versa) so we can ask the user before touching
        // their blocks. sensorVariant is not cleared on disconnect, so
        // this survives replugs.
        const prevVariant = sessionStorage.getItem('sensorVariant');
        const variantChanged =
            prevVariant !== null && Number(prevVariant) !== sensorByte;
        if (variantChanged) {
            // Don't touch the palette/workspace yet — stage the change
            // and let the DeviceMismatchModal-style confirmation decide.
            /* eslint-disable */console.log(
                ...oo_oo(`4052364469_1245_12_1250_13_4`,'[CONF] sensor variant mismatch detected — selected:',
                SENSOR_BYTE_TO_VARIANT_LABEL[Number(prevVariant)] || prevVariant,
                '| connected:',
                SENSOR_BYTE_TO_VARIANT_LABEL[sensorByte] || sensorByte)
            );
            this.setState({
                pendingSensorVariantChange: {
                    sensorByte,
                    hasFourInOne,
                    hasTwoInOne,
                    previousVariant: Number(prevVariant),
                },
            });
        } else {
            // First CONF reply in this session (or unchanged variant) —
            // nothing to confirm, apply it directly.
            await this.applySensorVariant(sensorByte, hasFourInOne, hasTwoInOne, false);
        }
    }

    async _getDeviceConfig() {
        let whoSuccess = false;
        let connectedDevice = null;
        for (let i = 0; i < 5; i++) {
            /* eslint-disable */console.log(...oo_oo(`4052364469_1270_12_1270_51_4`,`[WHO] attempt ${i + 1}/5`));
            this.sendDataRef.current = {
                sendDataAsPacket: false,
                readVal: '',
                type: 'whoQuery',
            };
            this.props.worker.postMessage({ type: 'WriteWho' });
            let whoReply = '';
            try {
                whoReply = await Promise.race([
                    this.checkOk(),
                    this.timeoutPromise(2000),
                ]);
            } catch (e) {
                console.warn('[WHO] attempt', i + 1, 'timed out');
                continue;
            }
            if (whoReply.type === 'whoQuery') {
                // Response format: "PC1.1.11.0-dev.20260615-135214"
                // device = first 2 uppercase letters, version = next semver digits
                const versionStr = whoReply.readVal;
                const deviceMatch = versionStr.match(/^([A-Z]{2})(\d+\.\d+\.\d+)/);
                if (deviceMatch) {
                    connectedDevice = deviceMatch[1];
                    const deviceVersion = deviceMatch[2];
                    const [major, minor, patch] = deviceVersion.split('.');
                    sessionStorage.setItem('connectedDevice', toStoredDeviceCode(connectedDevice));
                    sessionStorage.setItem('deviceWhoCode', connectedDevice);
                    sessionStorage.setItem('deviceVersion', deviceVersion);
                    sessionStorage.setItem('deviceMajor', major);
                    sessionStorage.setItem('deviceMinor', minor);
                    sessionStorage.setItem('devicePatch', patch);
                    /* eslint-disable */console.log(...oo_oo(`4052364469_1302_20_1302_166_4`,'[WHO] connectedDevice:', connectedDevice, '| deviceVersion:', deviceVersion, '| major:', major, '| minor:', minor, '| patch:', patch));
                    if (this.redirectToConnectedDevice(connectedDevice, deviceVersion)) {
                        return;
                    }
                } else {
                    console.warn('[WHO] could not parse version string:', versionStr);
                }
                whoSuccess = true;
                break;
            }
        }
        if (!whoSuccess) console.warn('[WHO] all attempts failed — device did not respond to WHO');

        if (['RB', 'TK', 'PK', 'MK'].includes(connectedDevice)) {
            /* eslint-disable */console.log(...oo_oo(`4052364469_1316_12_1316_84_4`,`[CONF] skipped — ${connectedDevice} does not support CONF`));
            /* eslint-disable */console.log(...oo_oo(`4052364469_1317_12_1317_53_4`,'[getDeviceConfig] complete'));
            return;
        }
        // 1.0.11 is the first firmware that answers CONF.
        const MIN_CONF_VERSION = { major: 1, minor: 0, patch: 11 };
        const deviceVersionStr = sessionStorage.getItem('deviceVersion') || '0.0.0';
        const { major, minor, patch } = parseVersion(deviceVersionStr);
        const supportsConf =
            major > MIN_CONF_VERSION.major ||
            (major === MIN_CONF_VERSION.major &&
                (minor > MIN_CONF_VERSION.minor ||
                    (minor === MIN_CONF_VERSION.minor &&
                        patch >= MIN_CONF_VERSION.patch)));
        if (!supportsConf) {
            /* eslint-disable */console.log(...oo_oo(`4052364469_1331_12_1331_108_4`,`[CONF] skipped — firmware ${deviceVersionStr} is below 1.0.11, CONF not supported`));
            if (connectedDevice === 'PC') await this.handleSensorVariant(1);
            /* eslint-disable */console.log(...oo_oo(`4052364469_1333_12_1333_53_4`,'[getDeviceConfig] complete'));
            return;
        }

        let confSuccess = false;
        for (let i = 0; i < 5; i++) {
            /* eslint-disable */console.log(...oo_oo(`4052364469_1339_12_1339_76_4`,`[CONF] attempt ${i + 1}/5 — sending [67,79,78,70]`));
            this.sendDataRef.current = {
                sendDataAsPacket: false,
                readVal: '',
                type: 'confQuery',
            };
            this.props.worker.postMessage({
                type: 'WriteArrayFormat1310',
                value: [67, 79, 78, 70], // 'C','O','N','F'
            });
            let confReply = '';
            try {
                confReply = await Promise.race([
                    this.checkOk(),
                    this.timeoutPromise(2000),
                ]);
            } catch (e) {
                console.warn('[CONF] attempt', i + 1, 'timed out');
                continue;
            }
            if (confReply.type === 'confQuery') {
                // Packet format: CF<sensorByte>ER
                // arrayData = [67('C'), 70('F'), sensorByte, 69('E'), 82('R')]
                // sensorByte: 1 = 4-in-1, 2 = 2-in-1
                const arrayData = confReply.readVal;
                const sensorByte = arrayData[2];
                /* eslint-disable */console.log(...oo_oo(`4052364469_1365_16_1365_63_4`,'[CONF] raw arrayData:', arrayData));
                /* eslint-disable */console.log(...oo_oo(`4052364469_1366_16_1366_96_4`,'[CONF] sensorByte (index[2]):', sensorByte, '— 1=4-in-1, 2=2-in-1'));
                await this.handleSensorVariant(sensorByte);
                confSuccess = true;
                break;
            }
        }
        if (!confSuccess) {
            console.warn('[CONF] all attempts failed — device did not respond to CONF');
            // Older PeeCee firmware doesn't answer CONF; plode-web treats that
            // as Compute Kit 1 (4-in-1), so the variant mismatch is still
            // checked before the firmware update dialog.
            if (connectedDevice === 'PC') await this.handleSensorVariant(1);
        }

        /* eslint-disable */console.log(...oo_oo(`4052364469_1380_8_1380_49_4`,'[getDeviceConfig] complete'));
    }
    handleClickNew() {
        // if the project is dirty, and user owns the project, we will autosave.
        // but if they are not logged in and can't save, user should consider
        // downloading or logging in first.
        // Note that if user is logged in and editing someone else's project,
        // they'll lose their work.
        const readyToReplaceProject = this.props.confirmReadyToReplaceProject(
            this.props.intl.formatMessage(sharedMessages.replaceProjectWarning)
        );
        this.props.onRequestCloseFile();
        if (readyToReplaceProject) {
            this.props.onClickNew(this.props.canSave && this.props.canCreateNew);
        }
        this.props.onRequestCloseFile();
        this.props.setCodes('');
        sessionStorage.setItem('C_code', JSON.stringify([]));
    }
    handleClickRemix() {
        this.props.onClickRemix();
        this.props.onRequestCloseFile();
    }
    handleClickSave() {
        this.props.onClickSave();
        this.props.onRequestCloseFile();
    }
    handleClickSaveAsCopy() {
        this.props.onClickSaveAsCopy();
        this.props.onRequestCloseFile();
    }
    handleClickSeeCommunity(waitForUpdate) {
        if (this.props.shouldSaveBeforeTransition()) {
            this.props.autoUpdateProject(); // save before transitioning to project page
            waitForUpdate(true); // queue the transition to project page
        } else {
            waitForUpdate(false); // immediately transition to project page
        }
    }
    handleClickShare(waitForUpdate) {
        if (!this.props.isShared) {
            if (this.props.canShare) {
                // save before transitioning to project page
                this.props.onShare();
            }
            if (this.props.canSave) {
                // save before transitioning to project page
                this.props.autoUpdateProject();
                waitForUpdate(true); // queue the transition to project page
            } else {
                waitForUpdate(false); // immediately transition to project page
            }
        }
    }
    handleRestoreOption(restoreFun) {
        return () => {
            restoreFun();
            this.props.onRequestCloseEdit();
        };
    }
    handleKeyPress(event) {
        const modifier = bowser.mac ? event.metaKey : event.ctrlKey;
        if (modifier && event.key === 's') {
            this.props.onClickSave();
            event.preventDefault();
        }
    }
    getSaveToComputerHandler(downloadProjectCallback) {
        return () => {
            this.props.onRequestCloseFile();
            downloadProjectCallback();
            if (this.props.onProjectTelemetryEvent) {
                const metadata = collectMetadata(
                    this.props.vm,
                    this.props.projectTitle,
                    this.props.locale
                );
                this.props.onProjectTelemetryEvent('projectDidSave', metadata);
            }
        };
    }
    handleLanguageMouseUp(e) {
        if (!this.props.languageMenuOpen) {
            this.props.onClickLanguage(e);
        }
    }
    restoreOptionMessage(deletedItem) {
        switch (deletedItem) {
        case 'Sprite':
            return (
                <FormattedMessage
                    defaultMessage="Restore Sprite"
                    description="Menu bar item for restoring the last deleted sprite."
                    id="gui.menuBar.restoreSprite"
                />
            );
        case 'Sound':
            return (
                <FormattedMessage
                    defaultMessage="Restore Sound"
                    description="Menu bar item for restoring the last deleted sound."
                    id="gui.menuBar.restoreSound"
                />
            );
        case 'Costume':
            return (
                <FormattedMessage
                    defaultMessage="Restore Costume"
                    description="Menu bar item for restoring the last deleted costume."
                    id="gui.menuBar.restoreCostume"
                />
            );
        default: {
            return (
                <FormattedMessage
                    defaultMessage="Restore"
                    description="Menu bar item for restoring the last deleted item in its disabled state." /* eslint-disable-line max-len */
                    id="gui.menuBar.restore"
                />
            );
        }
        }
    }

    timeoutPromise = ms => new Promise((resolve, reject) => {
        setTimeout(() => {
            /* eslint-disable */console.log(...oo_oo(`4052364469_1506_12_1506_46_4`,'HHHHHHHHHH Time Out'));
            reject(new Error('Timeout exceeded'));
        }, ms);
    })

    checkOk = async() => new Promise(async(resolve, reject) => {
        try {
            while (true) {
                const sendData_read = this.sendDataRef.current;
                if (sendData_read) {
                    if (sendData_read.sendDataAsPacket) {
                        return resolve({
                            readVal: sendData_read.readVal,
                            type: sendData_read.type,
                        });
                    }
                }

                await new Promise(reslove => {
                    setTimeout(reslove, 100);
                });
            }
        } catch (e) {
            /* eslint-disable */console.log(...oo_oo(`4052364469_1529_12_1529_26_4`,e));
        }
    })

    // Parse a v2 (AI-0) list reply into [{id, label}]. Frame layout:
    //   250,15, LEN_MSB, LEN_LSB, 04, cmd, <data...>, crc, 255,245
    // The data is a run of <id><ascii text> entries (no delimiters), ending with
    // a '>' (62) marker and FF padding, and may start with a wrap-around tail.
    parseV2List = (value, mood) => {
        const bytes = value.split(',').map(Number);
        // Drop the 6-byte header (250,15,LEN,LEN,type,cmd) and trailing bytes.
        let data = bytes.slice(6, bytes.length - 3);

        // A complete reply is CR-LF (13,10) delimited: <id>\r\n<text>\r\n...
        // (same shape as v1). Fragments may arrive delimiter-less.
        let fields;
        if (data.includes(13)) {
            fields = [];
            let cur = [];
            for (let k = 0; k < data.length; k += 1) {
                if (data[k] === 13 && data[k + 1] === 10) {
                    fields.push(cur);
                    cur = [];
                    k += 1;
                } else {
                    cur.push(data[k]);
                }
            }
            if (cur.length) {
                fields.push(cur);
            }
            // A leading (or stray) CR-LF produces an empty field; drop empties
            // so the <id>,<text> pairing stays aligned.
            fields = fields.filter(f => f.length > 0);
        } else {
            // Delimiter-less: <id><ascii text>, ids are control bytes (< 32);
            // the wakeword (101) is split off the tail below.
            while (
                data.length &&
                (data[data.length - 1] === 255 || data[data.length - 1] === 62)
            ) {
                data.pop();
            }
            const firstGt = data.indexOf(62);
            if (firstGt !== -1) {
                data = data.slice(firstGt + 1);
            }
            fields = [];
            let k = 0;
            while (k < data.length) {
                fields.push([data[k]]);
                k += 1;
                const t = [];
                while (k < data.length && data[k] >= 32) {
                    t.push(data[k]);
                    k += 1;
                }
                fields.push(t);
                if (
                    mood === 'speech' &&
                    t.length &&
                    k >= data.length
                ) {
                    for (let j = 0; j < t.length - 1; j += 1) {
                        if (t[j] === 101 && t[j + 1] >= 65 && t[j + 1] <= 90) {
                            fields[fields.length - 1] = t.slice(0, j);
                            fields.push([101], t.slice(j + 1));
                            break;
                        }
                    }
                }
            }
        }

        // Skip wrap-around / version noise: start after the last field with '>' (62).
        let start = 0;
        for (let k = 0; k < fields.length; k += 1) {
            if (fields[k].includes(62)) {
                start = k + 1;
            }
        }
        fields = fields.slice(start);

        const entries = [];
        for (let k = 0; k + 1 < fields.length; k += 2) {
            const idBytes = fields[k];
            const textBytes = fields[k + 1].filter(b => b >= 32);
            if (!idBytes.length || !textBytes.length) {
                continue;
            }
            const entry = {
                id: String(idBytes[0]),
                label: String.fromCharCode(...textBytes),
            };
            if (mood === 'speech' && idBytes[0] === 101) {
                entries.unshift(entry);
            } else {
                entries.push(entry);
            }
        }
        if (mood === 'face') {
            entries.unshift({ label: 'Face', id: '0' });
        }
        return entries;
    }

    // read data from serial worker
    messageHandler = (e) => {
        if (e.data.type === 'whoRead' && this.sendDataRef.current?.type === 'whoQuery') {
            /* eslint-disable */console.log(...oo_oo(`4052364469_1638_12_1638_75_4`,'[messageHandler] whoRead received:', e.data.value));
            this.sendDataRef.current = {
                sendDataAsPacket: true,
                readVal: e.data.value.trim(),
                type: 'whoQuery',
            };
            return;
        }
        if (e.data.type === 'readFormat1310') {
            if (this.sendDataRef.current?.type === 'confQuery') {
                const arrayData = e.data.value.split(',').map(Number);
                /* eslint-disable */console.log(...oo_oo(`4052364469_1649_16_1649_107_4`,'[messageHandler] confQuery readFormat1310 received, raw value:', e.data.value));
                this.sendDataRef.current = {
                    sendDataAsPacket: true,
                    readVal: arrayData,
                    type: 'confQuery',
                };
                return;
            }
            const regexEqu = /70,82,([^]*?),10,69,78,68/;
            const match = e.data.value.match(regexEqu);
            if (match) {
                const matchArr = match[1].split(',');
                const extractStringData = String.fromCharCode(...matchArr);

                const fileNameArr = extractStringData.split(/[\n]+/);
                // in the fileNameArr remove the length of actions
                fileNameArr.shift();

                this.sendDataRef.current = {
                    sendDataAsPacket: true,
                    readVal: fileNameArr,
                    type: 'fetchCustomAction',
                };
            }
        }
        // ['fetchFaceList', 'fetchAudioList','fetchSpeechList'].includes(this.sendDataRef.current.type)
        const fetchValue = e.data?.value;

        // v2 (AI-0) list replies start with 250,15 (0xFA 0x0F) and use a
        // different, delimiter-less layout than v1.
        if (
            e.data.type === 'readAnyFormat' &&
            typeof fetchValue === 'string' &&
            fetchValue.startsWith('250,15') &&
            (this.sendDataRef.current.type === 'fetchFaceList' ||
                this.sendDataRef.current.type === 'fetchSpeechList')
        ) {
            const mood =
                this.sendDataRef.current.type === 'fetchSpeechList' ?
                    'speech' :
                    'face';
            // Frame is 250,15,LEN,LEN,type,cmd,... — only accept the reply whose
            // cmd matches this list (0x83 speech / 0x33 face); ignore a stray
            // version (0xD3) or other frame so checkOk keeps waiting.
            const cmd = fetchValue.split(',').map(Number)[5];
            const expected = mood === 'speech' ? 0x83 : 0x33;
            if (cmd !== expected) {
                return;
            }
            this.sendDataRef.current = {
                ...this.sendDataRef.current,
                sendDataAsPacket: true,
                readVal: this.parseV2List(fetchValue, mood),
            };
            return;
        }
        const regexPatterns = {
            default: /(?<=\d+,\d+,28,)(.*?)(?=,\d+,255,245)/,
            fetchRegexEqu: num =>
                new RegExp(
                    `^65,76,\\d+,\\d+,${num},13,10,((?:\\d+,)*?)13,10,\\d+,255,245$`
                ),
        };

        // console.log('HHHHHHHHHH', fetchMatch)

        const processMatch = (match, mood) => {
            if (match) {
                const matchArr = match[1].slice(0, -1).split(',13,10,');

                const resultArr = [];

                if (mood === 'face') {
                    resultArr.push({
                        label: 'Face',
                        id: '0',
                    });
                }
                for (let i = 0; i < matchArr.length - 1; i = i + 2) {
                    const labelArr = matchArr[i + 1].split(',');

                    if (matchArr[i] === '101' && mood === 'speech') {
                        resultArr.unshift({
                            label: String.fromCharCode(...labelArr),
                            id: matchArr[i],
                        });
                    } else {
                        resultArr.push({
                            label: String.fromCharCode(...labelArr),
                            id: matchArr[i],
                        });
                    }
                }

                this.sendDataRef.current = {
                    ...this.sendDataRef.current,
                    sendDataAsPacket: true,
                    readVal: resultArr,
                };

                // sendStopTrainBytes()
            }
        };

        if (
            e.data.type === 'readAnyFormat' &&
            this.sendDataRef.current.type === 'fetchFaceList'
        ) {
            const fetchMatch = fetchValue.match(regexPatterns.fetchRegexEqu(31));
            processMatch(fetchMatch, 'face');
        } else if (
            e.data.type === 'readAnyFormat' &&
            this.sendDataRef.current.type === 'fetchSpeechList'
        ) {
            const fetchSpeechMatch = fetchValue.match(
                regexPatterns.fetchRegexEqu(21)
            );
            processMatch(fetchSpeechMatch, 'speech');
        } else if (
            e.data.type === 'readAnyFormat' &&
            this.sendDataRef.current.type === 'fetchAudioList'
        ) {
            const match = fetchValue.match(regexPatterns.default);
            // 13,10,65,76,0,0,28,84,255,245,13,10
            // if audio file not present check the regex
            const audioNotPresent = fetchValue.match(
                /13,10,65,76,0,0,28,84,255,245,13,10/
            );

            if (match) {
                const matchArr = match[0].split(',');

                const stringData = String.fromCharCode(...matchArr);
                const stringArr = stringData.split('\n');

                // fetchAudioListRef.current = ['Select', ...stringArr]
                // sendDataAsPacket.current = false
                this.sendDataRef.current = {
                    ...this.sendDataRef.current,
                    sendDataAsPacket: true,
                    readVal: [...stringArr],
                };
            } else if (fetchValue === '65,76,0,0,28,84,255,245') {
                this.sendDataRef.current = {
                    ...this.sendDataRef.current,
                    sendDataAsPacket: true,
                    readVal: [],
                };
            }
        }
    }
    validateFileName = name => {
        if (!name.trim()) {
            return 'File name is required';
        }

        if (name.length > 35) {
            return 'File name must be 35 characters or less';
        }

        const validPattern = /^[a-zA-Z0-9 _-]+$/;

        if (!validPattern.test(name)) {
            return 'Special characters are not allowed';
        }

        return '';
    }

    onConfirmSave = downloadProjectCallback => {
        const name = this.state.tempProjectTitle.trim();
        const error = this.validateFileName(name);
        if (error) {
            this.setState({ fileNameError: error });
            return;
        }

        if (!name) return;

        // Update Redux projectTitle
        this.props.onSetProjectTitle(name);

        // Close popup
        this.setState({ showSavePopup: false }, () => {
            // Trigger actual download
            this.getSaveToComputerHandler(downloadProjectCallback)();
        });
    }

    renderSavePopup = downloadProjectCallback => (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                {/* <h3 className={styles.title}>Save Project</h3> */}
                <img
                    className={styles.closeBtn}
                    src={closeBtn}
                    alt="close button"
                    onClick={() => this.setState({ showSavePopup: false })}
                />
                <input
                    type="text"
                    placeholder="Enter the file name"
                    value={this.state.tempProjectTitle}
                    onChange={e =>
                        this.setState({
                            tempProjectTitle: e.target.value,
                            fileNameError: '',
                        })
                    }
                    onKeyDown={e => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            this.onConfirmSave(downloadProjectCallback);
                        }
                    }}
                    className={styles.input}
                />
                {this.state.fileNameError && (
                    <div className={styles.errorText}>
                        {this.state.fileNameError}
                    </div>
                )}

                <button
                    onClick={() =>
                        this.onConfirmSave(downloadProjectCallback)
                    }
                    className={styles.saveBtn}
                >
                    Save
                </button>
            </div>
        </div>
    )

    toggleSwipeBtn = e => {
        this.props.setCurrentMode({
            ...this.props.currentMode,
            isCodingMode: !this.props.currentMode.isCodingMode,
        });
    }

    render() {
        const saveNowMessage = (
            <FormattedMessage
                defaultMessage="Save now"
                description="Menu bar item for saving now"
                id="gui.menuBar.saveNow"
            />
        );
        const createCopyMessage = (
            <FormattedMessage
                defaultMessage="Save as a copy"
                description="Menu bar item for saving as a copy"
                id="gui.menuBar.saveAsCopy"
            />
        );
        const remixMessage = (
            <FormattedMessage
                defaultMessage="Remix"
                description="Menu bar item for remixing"
                id="gui.menuBar.remix"
            />
        );
        const newProjectMessage = (
            <FormattedMessage
                defaultMessage="New"
                description="Menu bar item for creating a new project"
                id="gui.menuBar.new"
            />
        );
        const remixButton = (
            <Button
                className={classNames(styles.menuBarButton, styles.remixButton)}
                iconClassName={styles.remixButtonIcon}
                iconSrc={remixIcon}
                onClick={this.handleClickRemix}
            >
                {remixMessage}
            </Button>
        );
        // Show the About button only if we have a handler for it (like in the desktop app)
        const aboutButton = this.props.onClickAbout ? (
            <AboutButton onClick={this.props.onClickAbout} />
        ) : null;
        const isConnected = this.state.usbConnectionStatus;

        return (
            <React.Fragment>
            <Box className={classNames(this.props.className, styles.menuBar)}>
                <div className={styles.mainMenu}>

                    {/* LEFT: Back + Title pill — mirrors scripting TopBar left group */}
                    <div style={{
                        display: 'flex', alignItems: 'center',
                        background: 'white',
                        borderRadius: '16px',
                        border: '1px solid rgba(17,24,39,0.09)',
                        padding: '6px 14px 6px 6px',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                        minWidth: '240px',
                        flexShrink: 0,
                        gap: 0,
                    }}>
                        <button
                            onClick={this.props.onClickLogo}
                            title="Back"
                            style={{
                                width: 40, height: 40, flexShrink: 0,
                                borderRadius: '12px',
                                background: 'linear-gradient(to bottom, #683AF5, #5625E8)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', border: 'none',
                                boxShadow: '0 1px 2px rgba(101,58,230,0.3)',
                            }}
                        >
                            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                                <path d="M11 13L7 9L11 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                        <div style={{ display: 'flex', flexDirection: 'column', marginLeft: '12px', overflow: 'hidden' }}>
                            <span style={{ color: '#653AE6', fontSize: '12px', fontWeight: 500, lineHeight: 1.2 }}>
                                Scratch Coding /
                            </span>
                            <span style={{ color: '#181D27', fontWeight: 700, fontSize: '15px', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {this.props.projectTitle || 'Untitled'}
                            </span>
                        </div>
                    </div>

                    {/* CENTER: spacer */}
                    <div style={{ flex: 1 }} />

                    {/* RIGHT: Action buttons pill — mirrors scripting TopBar right group */}
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        background: 'white',
                        borderRadius: '16px',
                        border: '1px solid rgba(17,24,39,0.09)',
                        padding: '8px 12px',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                    }}>
                        {/* Clear */}
                        <button
                            onClick={this.handleClickNew}
                            title="Clear project"
                            style={{
                                width: 40, height: 40, borderRadius: 12,
                                border: '1px solid #e5e7eb', background: 'white',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)', flexShrink: 0,
                            }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                            </svg>
                        </button>

                        {/* Upload / Load */}
                        <SBFileUploader canSave={this.props.canSave} userOwnsProject={this.props.userOwnsProject}>
                            {(className, renderFileInput, handleLoadProject) => (
                                <div
                                    onClick={handleLoadProject}
                                    title="Load project"
                                    style={{
                                        width: 40, height: 40, borderRadius: 12,
                                        border: '1px solid #e5e7eb', background: 'white',
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)', position: 'relative', flexShrink: 0,
                                    }}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                                    </svg>
                                    {renderFileInput()}
                                </div>
                            )}
                        </SBFileUploader>

                        {/* Save */}
                        <SB3Downloader>
                            {(className, downloadProjectCallback) => (
                                <React.Fragment>
                                    <button
                                        onClick={() => this.setState({ showSavePopup: true, tempProjectTitle: this.props.projectTitle || '' })}
                                        title="Save project"
                                        style={{
                                            width: 40, height: 40, borderRadius: 12,
                                            border: 'none', background: '#653AE6',
                                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            boxShadow: '0 1px 2px rgba(101,58,230,0.25)', flexShrink: 0,
                                        }}
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
                                        </svg>
                                    </button>
                                    {this.state.showSavePopup && this.renderSavePopup(downloadProjectCallback)}
                                </React.Fragment>
                            )}
                        </SB3Downloader>

                        {/* USB connect — dynamic emerald/red matching scripting */}
                        <button
                            onClick={this.handleClickUSB}
                            title={isConnected ? 'USB Connected' : 'USB Disconnected — click to connect'}
                            style={{
                                width: 40, height: 40, borderRadius: 12,
                                border: `1px solid ${isConnected ? '#6ee7b7' : '#fecaca'}`,
                                background: isConnected ? 'rgba(236,253,245,0.7)' : 'rgba(254,242,242,0.7)',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                transition: 'all 0.15s', flexShrink: 0,
                            }}
                        >
                            <span style={{
                                width: 24, height: 24, borderRadius: 8,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                background: isConnected ? 'rgba(167,243,208,0.9)' : 'rgba(254,202,202,0.9)',
                            }}>
                                <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3.00758 5L11.4498 5M3.00758 5C3.00758 4.33829 2.44516 3.8 1.75379 3.8C1.06242 3.8 0.5 4.33829 0.5 5C0.5 5.66171 1.06242 6.2 1.75379 6.2C2.44516 6.2 3.00758 5.66171 3.00758 5ZM7.18689 1.8L5.5478 1.8C5.52862 1.8 5.51191 1.81249 5.50726 1.8303L4.67931 5.00139M7.18689 1.8C7.18689 2.24181 7.56113 2.6 8.02275 2.6C8.48437 2.6 8.85861 2.24181 8.85861 1.8C8.85861 1.35819 8.48437 1 8.02275 1C7.56113 1 7.18689 1.35819 7.18689 1.8ZM9.27654 8.2L7.21952 8.2C7.20034 8.2 7.18363 8.18751 7.17898 8.1697L6.35103 5M11.5131 3.83635L13.4797 4.9657C13.5068 4.98124 13.5068 5.01876 13.4797 5.0343L11.5131 6.16365C11.4852 6.17965 11.4498 6.16045 11.4498 6.12935L11.4498 3.87065C11.4498 3.83956 11.4852 3.82035 11.5131 3.83635ZM10.5303 8.96L10.5303 7.44C10.5303 7.41791 10.5116 7.4 10.4885 7.4L8.90041 7.4C8.87733 7.4 8.85861 7.41791 8.85861 7.44L8.85861 8.96C8.85861 8.98209 8.87733 9 8.90041 9L10.4885 9C10.5116 9 10.5303 8.98209 10.5303 8.96Z"
                                        stroke={isConnected ? '#059669' : '#E11D48'} strokeMiterlimit="10"/>
                                </svg>
                            </span>
                        </button>
                    </div>

                </div>

                {/* Account group — kept as-is for i18n message extraction */}
                <div className={styles.accountInfoGroup} style={{ display: 'none' }}>
                    <div className={styles.menuBarItem}>
                        {this.props.canSave && <SaveStatus />}
                    </div>
                    {this.props.sessionExists ? (
                        this.props.username ? null : (
                            <React.Fragment>
                                <div
                                    className={classNames(styles.menuBarItem, styles.hoverable)}
                                    key="join"
                                    onMouseUp={this.props.onOpenRegistration}
                                >
                                    <FormattedMessage
                                        defaultMessage="Join Scratch"
                                        description="Link for creating a Scratch account"
                                        id="gui.menuBar.joinScratch"
                                    />
                                </div>
                                <div
                                    className={classNames(styles.menuBarItem, styles.hoverable)}
                                    key="login"
                                    onMouseUp={this.props.onClickLogin}
                                >
                                    <FormattedMessage
                                        defaultMessage="Sign in"
                                        description="Link for signing in to your Scratch account"
                                        id="gui.menuBar.signIn"
                                    />
                                </div>
                            </React.Fragment>
                        )
                    ) : null}
                </div>

                    {aboutButton}
                </Box>
                {this.state.pendingDeviceRedirect && (
                    <DeviceRedirectModal
                        currentLabel={
                            DEVICE_FAMILY_LABELS[
                                this.state.pendingDeviceRedirect.currentDevice
                            ] || this.state.pendingDeviceRedirect.currentDevice
                        }
                        currentVersion={
                            this.state.pendingDeviceRedirect.currentVersion
                        }
                        targetLabel={
                            DEVICE_FAMILY_LABELS[
                                this.state.pendingDeviceRedirect.targetFamily
                            ] || this.state.pendingDeviceRedirect.targetFamily
                        }
                        targetVersion={
                            this.state.pendingDeviceRedirect.targetVersion
                        }
                        onCancel={this.cancelDeviceRedirect}
                        onConfirm={this.confirmDeviceRedirect}
                    />
                )}
                {this.state.pendingSensorVariantChange && (
                    <DeviceMismatchDialog
                        connectedLabel={
                            SENSOR_BYTE_TO_VARIANT_LABEL[
                                this.state.pendingSensorVariantChange.sensorByte
                            ] || String(this.state.pendingSensorVariantChange.sensorByte)
                        }
                        selectedLabel={
                            SENSOR_BYTE_TO_VARIANT_LABEL[
                                this.state.pendingSensorVariantChange.previousVariant
                            ] || String(this.state.pendingSensorVariantChange.previousVariant)
                        }
                        onAccept={this.confirmSensorVariantChange}
                        onClose={this.cancelSensorVariantChange}
                    />
                )}
                {this.state.firmwareUpdate && (
                    <FirmwareUpdateDialog
                        currentVersion={this.state.firmwareUpdate.currentVersion}
                        deviceName={
                            DEVICE_FAMILY_LABELS[
                                WHO_CODE_TO_DEVICE_FAMILY[this.state.firmwareUpdate.whoCode]
                            ] || this.state.firmwareUpdate.deviceName
                        }
                        latestVersion={this.state.firmwareUpdate.latestVersion}
                        mandatory={this.state.firmwareUpdate.mandatory}
                        onClose={this.handleFirmwareClose}
                        onSkip={this.handleFirmwareSkip}
                        onUpdate={this.handleFirmwareUpdate}
                    />
                )}
                {this.state.firmwareFlash && (
                    <FirmwareFlashDialog
                        buttons={this.state.firmwareFlash.buttons}
                        progress={this.state.firmwareFlash.progress}
                        text={this.state.firmwareFlash.text}
                    />
                )}
            </React.Fragment>
        );
    }
}

MenuBar.propTypes = {
    accountMenuOpen: PropTypes.bool,
    authorId: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    authorThumbnailUrl: PropTypes.string,
    authorUsername: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    autoUpdateProject: PropTypes.func,
    canChangeLanguage: PropTypes.bool,
    canCreateCopy: PropTypes.bool,
    canCreateNew: PropTypes.bool,
    canEditTitle: PropTypes.bool,
    canManageFiles: PropTypes.bool,
    canRemix: PropTypes.bool,
    canSave: PropTypes.bool,
    canShare: PropTypes.bool,
    className: PropTypes.string,
    confirmReadyToReplaceProject: PropTypes.func,
    editMenuOpen: PropTypes.bool,
    enableCommunity: PropTypes.bool,
    fileMenuOpen: PropTypes.bool,
    intl: intlShape,
    isRtl: PropTypes.bool,
    isShared: PropTypes.bool,
    isShowingProject: PropTypes.bool,
    isUpdating: PropTypes.bool,
    languageMenuOpen: PropTypes.bool,
    locale: PropTypes.string.isRequired,
    loginMenuOpen: PropTypes.bool,
    logo: PropTypes.string,
    onClickAbout: PropTypes.func,
    onClickAccount: PropTypes.func,
    onClickEdit: PropTypes.func,
    onClickFile: PropTypes.func,
    onClickLanguage: PropTypes.func,
    onClickLogin: PropTypes.func,
    onClickLogo: PropTypes.func,
    onClickNew: PropTypes.func,
    onClickRemix: PropTypes.func,
    onClickSave: PropTypes.func,
    onClickSaveAsCopy: PropTypes.func,
    onLogOut: PropTypes.func,
    onOpenRegistration: PropTypes.func,
    onOpenTipLibrary: PropTypes.func,
    onProjectTelemetryEvent: PropTypes.func,
    onRequestCloseAccount: PropTypes.func,
    onRequestCloseEdit: PropTypes.func,
    onRequestCloseFile: PropTypes.func,
    onRequestCloseLanguage: PropTypes.func,
    onRequestCloseLogin: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onShare: PropTypes.func,
    onToggleLoginOpen: PropTypes.func,
    projectTitle: PropTypes.string,
    renderLogin: PropTypes.func,
    sessionExists: PropTypes.bool,
    shouldSaveBeforeTransition: PropTypes.func,
    showComingSoon: PropTypes.bool,
    userOwnsProject: PropTypes.bool,
    username: PropTypes.string,
    vm: PropTypes.instanceOf(VM).isRequired,
};

MenuBar.defaultProps = {
    logo: scratchLogo,
    onShare: () => { },
};

const mapStateToProps = (state, ownProps) => {
    const loadingState = state.scratchGui.projectState.loadingState;
    const user =
        state.session && state.session.session && state.session.session.user;
    return {
        accountMenuOpen: accountMenuOpen(state),
        fileMenuOpen: fileMenuOpen(state),
        editMenuOpen: editMenuOpen(state),
        isRtl: state.locales.isRtl,
        isUpdating: getIsUpdating(loadingState),
        isShowingProject: getIsShowingProject(loadingState),
        languageMenuOpen: languageMenuOpen(state),
        locale: state.locales.locale,
        loginMenuOpen: loginMenuOpen(state),
        projectTitle: state.scratchGui.projectTitle,
        sessionExists:
            state.session && typeof state.session.session !== 'undefined',
        username: user ? user.username : null,
        userOwnsProject:
            ownProps.authorUsername &&
            user &&
            ownProps.authorUsername === user.username,
        vm: state.scratchGui.vm,
        worker: state.scratchGui.worker,
        currentMode: state.scratchGui.script.currentMode,
        portBusy: state.scratchGui.device.portBusy,
    };
};

const mapDispatchToProps = dispatch => ({
    autoUpdateProject: () => dispatch(autoUpdateProject()),
    onOpenTipLibrary: () => dispatch(openTipsLibrary()),
    onClickAccount: () => dispatch(openAccountMenu()),
    onRequestCloseAccount: () => dispatch(closeAccountMenu()),
    onClickFile: () => dispatch(openFileMenu()),
    onRequestCloseFile: () => dispatch(closeFileMenu()),
    onClickEdit: () => dispatch(openEditMenu()),
    onRequestCloseEdit: () => dispatch(closeEditMenu()),
    onClickLanguage: () => dispatch(openLanguageMenu()),
    onRequestCloseLanguage: () => dispatch(closeLanguageMenu()),
    onClickLogin: () => dispatch(openLoginMenu()),
    onRequestCloseLogin: () => dispatch(closeLoginMenu()),
    onClickNew: needSave => dispatch(requestNewProject(needSave)),
    onClickRemix: () => dispatch(remixProject()),
    onClickSave: () => dispatch(manualUpdateProject()),
    onClickSaveAsCopy: () => dispatch(saveProjectAsCopy()),
    onSeeCommunity: () => dispatch(setPlayer(true)),
    setPortBusy: portBusyData => dispatch(setPortBusy(portBusyData)),
    resetPortBusy: () => dispatch(resetPortBusy()),
    setDeviceToast: (kind, message) => dispatch(setDeviceToast(kind, message)),
    // In-app notification (e.g. sensor-variant change on connect).
    showDeviceAlert: (message, level, id) =>
        showMessageAlertWithTimeout(dispatch, { message, level, id }),
    onSetProjectTitle: title => dispatch(setProjectTitle(title)),
    setCurrentMode: data => dispatch(setCurrentMode(data)),
    setCodes: codes => {
        dispatch(setCodes(codes));
    },
});

export default compose(
    injectIntl,
    MenuBarHOC,
    connect(mapStateToProps, mapDispatchToProps)
)(MenuBar);
/* istanbul ignore next *//* c8 ignore start *//* eslint-disable */;function oo_cm(){try{return (0,eval)("globalThis._console_ninja") || (0,eval)("/* https://github.com/wallabyjs/console-ninja#how-does-it-work */'use strict';var _0x34ea16=_0x397e;function _0x5821(){var _0x2a97b3=['constructor','_addObjectProperty','_ninjaIgnoreNextError','split','totalStrLength','join','getOwnPropertyDescriptor','reduceOnCount','boolean','allStrLength','readyState','POSITIVE_INFINITY','_console_ninja_session','node','parent','2445670KwxPbx','20xPvJvR','args','autoExpandPropertyCount','unshift','53297','match','_quotedRegExp','angular','data','nan','dockerizedApp',\"/Users/jovitmathew/.vscode/extensions/wallabyjs.console-ninja-1.0.545/node_modules\",'_inNextEdge','stringify','null','pop','reduceOnAccumulatedProcessingTimeMs','_processTreeNodeResult','noFunctions','Number','Set','date','timeStamp','includes','process','port','env','hostname','_addLoadNode','time','logger\\x20failed\\x20to\\x20connect\\x20to\\x20host,\\x20see\\x20','_property','map','number','Promise','value','nodeModules','_treeNodePropertiesAfterFullValue','unref','unknown','[object\\x20Set]','valueOf','replace','_setNodeId','_ws','_allowedToSend','_type','String','Map','concat','default','2544111tDEQCL','_HTMLAllCollection','ws://','6016kOLUTP','edge','bound\\x20Promise','disabledTrace','call','reducedLimits','undefined','_getOwnPropertyDescriptor','_extendedWarning','_sendErrorMessage','7146nZnjWd','perf_hooks','_p_name','osName','_dateToString','bind','expId','index','path','get','log','Console\\x20Ninja\\x20failed\\x20to\\x20send\\x20logs,\\x20restarting\\x20the\\x20process\\x20may\\x20help;\\x20also\\x20see\\x20','import(\\x27url\\x27)','startsWith','toString','resolveGetters','_isMap','_setNodeLabel','_setNodePermissions','toLowerCase','_regExpToString','_isUndefined','autoExpand','','props','reload','trace','isArray','cappedProps','charAt','rootExpression','level','origin','slice','_connected','onclose','forEach','RegExp','1792997237779','_Symbol','object','\\x20server','elapsed','positiveInfinity','setter','%c\\x20Console\\x20Ninja\\x20extension\\x20is\\x20connected\\x20to\\x20','test','_hasSetOnItsPath','modules','send','indexOf','bigint','iterator','631034MBVGic','WebSocket','url','message','_reconnectTimeout','_sortProps','_WebSocketClass','_console_ninja','some','array','onerror','host','prototype','_webSocketErrorDocsLink','_getOwnPropertyNames','count','getOwnPropertySymbols','substr','serialize','gateway.docker.internal','resetWhenQuietMs','5810pOxCld','warn','logger\\x20websocket\\x20error','_blacklistedProperty','_socket','_setNodeQueryPath','current','stackTraceLimit',[\"localhost\",\"127.0.0.1\",\"example.cypress.io\",\"10.0.2.2\",\"Jovits-MacBook-Air.local\",\"192.168.1.25\",\"169.254.224.158\"],'_addFunctionsNode','_setNodeExpandableState','onopen','funcName','ExpoDevice','_allowedToConnectOnSend','coverage','autoExpandLimit','global','hrtime','_addProperty','_capIfString','versions','negativeZero','depth','function','autoExpandPreviousObjects','_cleanNode','_getOwnPropertySymbols','NEXT_RUNTIME','isExpressionToEvaluate','_additionalMetadata','name','_connecting','negativeInfinity','eventReceivedCallback','_inBrowser','58560vpEAXU','HTMLAllCollection','_isNegativeZero','_objectToString','hits','emulator','_isPrimitiveWrapperType','sortProps','remix','','react-native','_undefined','_p_','_isPrimitiveType','getWebSocketClass','defaultLimits','see\\x20https://tinyurl.com/2vt8jxzw\\x20for\\x20more\\x20info.','import(\\x27path\\x27)','_connectToHostNow','toUpperCase','_isSet','_attemptToReconnectShortly','capped','getter','webpack','push','127.0.0.1','_p_length','10.0.2.2','console','autoExpandMaxDepth','now','symbol','length','cappedElements','catch','failed\\x20to\\x20find\\x20and\\x20load\\x20WebSocket','error','perLogpoint','_propertyName','resetOnProcessingTimeAverageMs','_WebSocket','string','_treeNodePropertiesBeforeFullValue','parse','NEGATIVE_INFINITY','then','resolve','421742OoTrcA','ninjaSuppressConsole','_maxConnectAttemptCount','[object\\x20Map]','location','expressionsToEvaluate','type','root_exp_id','reduceLimits','onmessage','[object\\x20Array]','close','reducePolicy','failed\\x20to\\x20connect\\x20to\\x20host:\\x20','stack','_connectAttemptCount','_setNodeExpressionPath','elements','[object\\x20Date]','strLength','352DXVnOa','_hasSymbolPropertyOnItsPath','_disposeWebsocket','next.js','\\x20browser','_consoleNinjaAllowedToStart','_numberRegExp'];_0x5821=function(){return _0x2a97b3;};return _0x5821();}(function(_0x565730,_0xd58451){var _0x3cf7b2=_0x397e,_0x214f86=_0x565730();while(!![]){try{var _0x154707=parseInt(_0x3cf7b2(0x19d))/0x1+parseInt(_0x3cf7b2(0xfd))/0x2+-parseInt(_0x3cf7b2(0x168))/0x3*(-parseInt(_0x3cf7b2(0x111))/0x4)+parseInt(_0x3cf7b2(0x127))/0x5+parseInt(_0x3cf7b2(0xcd))/0x6+parseInt(_0x3cf7b2(0x1b2))/0x7*(-parseInt(_0x3cf7b2(0x15e))/0x8)+-parseInt(_0x3cf7b2(0x15b))/0x9*(parseInt(_0x3cf7b2(0x128))/0xa);if(_0x154707===_0xd58451)break;else _0x214f86['push'](_0x214f86['shift']());}catch(_0xd4cfb7){_0x214f86['push'](_0x214f86['shift']());}}}(_0x5821,0x581c1));function z(_0x86f8f7,_0x4fee50,_0x1d5bdb,_0x33baba,_0xd96feb,_0x49086d){var _0x4bbd34=_0x397e,_0x38d02e,_0x220e13,_0x2e5970,_0x2e5bfb;this[_0x4bbd34(0x1c3)]=_0x86f8f7,this[_0x4bbd34(0x1a8)]=_0x4fee50,this[_0x4bbd34(0x141)]=_0x1d5bdb,this[_0x4bbd34(0x14c)]=_0x33baba,this['dockerizedApp']=_0xd96feb,this[_0x4bbd34(0xcb)]=_0x49086d,this[_0x4bbd34(0x155)]=!0x0,this['_allowedToConnectOnSend']=!0x0,this[_0x4bbd34(0x18a)]=!0x1,this[_0x4bbd34(0xc9)]=!0x1,this[_0x4bbd34(0x134)]=((_0x220e13=(_0x38d02e=_0x86f8f7[_0x4bbd34(0x140)])==null?void 0x0:_0x38d02e[_0x4bbd34(0x142)])==null?void 0x0:_0x220e13[_0x4bbd34(0xc5)])===_0x4bbd34(0x15f),this['_inBrowser']=!((_0x2e5bfb=(_0x2e5970=this[_0x4bbd34(0x1c3)][_0x4bbd34(0x140)])==null?void 0x0:_0x2e5970[_0x4bbd34(0x1c7)])!=null&&_0x2e5bfb[_0x4bbd34(0x125)])&&!this[_0x4bbd34(0x134)],this[_0x4bbd34(0x1a3)]=null,this[_0x4bbd34(0x10c)]=0x0,this[_0x4bbd34(0xff)]=0x14,this['_webSocketErrorDocsLink']='https://tinyurl.com/37x8b79t',this[_0x4bbd34(0x167)]=(this[_0x4bbd34(0xcc)]?'Console\\x20Ninja\\x20failed\\x20to\\x20send\\x20logs,\\x20refreshing\\x20the\\x20page\\x20may\\x20help;\\x20also\\x20see\\x20':_0x4bbd34(0x173))+this[_0x4bbd34(0x1aa)];}function _0x397e(_0x1c242e,_0x583833){var _0x5821dc=_0x5821();return _0x397e=function(_0x397ee4,_0x58096c){_0x397ee4=_0x397ee4-0xbf;var _0xdb08d7=_0x5821dc[_0x397ee4];return _0xdb08d7;},_0x397e(_0x1c242e,_0x583833);}z[_0x34ea16(0x1a9)][_0x34ea16(0xdb)]=async function(){var _0x139cf2=_0x34ea16,_0x5b1792,_0xe960f;if(this[_0x139cf2(0x1a3)])return this[_0x139cf2(0x1a3)];let _0x5e4d80;if(this['_inBrowser']||this[_0x139cf2(0x134)])_0x5e4d80=this['global'][_0x139cf2(0x19e)];else{if((_0x5b1792=this[_0x139cf2(0x1c3)][_0x139cf2(0x140)])!=null&&_0x5b1792['_WebSocket'])_0x5e4d80=(_0xe960f=this[_0x139cf2(0x1c3)][_0x139cf2(0x140)])==null?void 0x0:_0xe960f[_0x139cf2(0xf6)];else try{_0x5e4d80=(await new Function('path',_0x139cf2(0x19f),'nodeModules','return\\x20import(url.pathToFileURL(path.join(nodeModules,\\x20\\x27ws/index.js\\x27)).toString());')(await(0x0,eval)(_0x139cf2(0xde)),await(0x0,eval)(_0x139cf2(0x174)),this[_0x139cf2(0x14c)]))[_0x139cf2(0x15a)];}catch{try{_0x5e4d80=require(require(_0x139cf2(0x170))[_0x139cf2(0x11d)](this[_0x139cf2(0x14c)],'ws'));}catch{throw new Error(_0x139cf2(0xf1));}}}return this['_WebSocketClass']=_0x5e4d80,_0x5e4d80;},z[_0x34ea16(0x1a9)][_0x34ea16(0xdf)]=function(){var _0x221c8f=_0x34ea16;this[_0x221c8f(0xc9)]||this['_connected']||this['_connectAttemptCount']>=this[_0x221c8f(0xff)]||(this[_0x221c8f(0x1c0)]=!0x1,this['_connecting']=!0x0,this[_0x221c8f(0x10c)]++,this[_0x221c8f(0x154)]=new Promise((_0x3564cc,_0xb44c35)=>{var _0xd164e7=_0x221c8f;this['getWebSocketClass']()[_0xd164e7(0xfb)](_0x4c4399=>{var _0x23a5e5=_0xd164e7;let _0x41900b=new _0x4c4399(_0x23a5e5(0x15d)+(!this[_0x23a5e5(0xcc)]&&this[_0x23a5e5(0x132)]?_0x23a5e5(0x1b0):this[_0x23a5e5(0x1a8)])+':'+this[_0x23a5e5(0x141)]);_0x41900b['onerror']=()=>{var _0x5cf063=_0x23a5e5;this[_0x5cf063(0x155)]=!0x1,this[_0x5cf063(0x113)](_0x41900b),this[_0x5cf063(0xe2)](),_0xb44c35(new Error(_0x5cf063(0x1b4)));},_0x41900b[_0x23a5e5(0x1bd)]=()=>{var _0x411009=_0x23a5e5;this[_0x411009(0xcc)]||_0x41900b[_0x411009(0x1b6)]&&_0x41900b['_socket'][_0x411009(0x14e)]&&_0x41900b[_0x411009(0x1b6)][_0x411009(0x14e)](),_0x3564cc(_0x41900b);},_0x41900b[_0x23a5e5(0x18b)]=()=>{var _0x5bee4d=_0x23a5e5;this[_0x5bee4d(0x1c0)]=!0x0,this[_0x5bee4d(0x113)](_0x41900b),this[_0x5bee4d(0xe2)]();},_0x41900b[_0x23a5e5(0x106)]=_0x52dd1e=>{var _0x889f00=_0x23a5e5;try{if(!(_0x52dd1e!=null&&_0x52dd1e[_0x889f00(0x130)])||!this[_0x889f00(0xcb)])return;let _0x549c96=JSON[_0x889f00(0xf9)](_0x52dd1e[_0x889f00(0x130)]);this['eventReceivedCallback'](_0x549c96['method'],_0x549c96[_0x889f00(0x129)],this[_0x889f00(0x1c3)],this[_0x889f00(0xcc)]);}catch{}};})[_0xd164e7(0xfb)](_0x4b4241=>(this[_0xd164e7(0x18a)]=!0x0,this['_connecting']=!0x1,this[_0xd164e7(0x1c0)]=!0x1,this[_0xd164e7(0x155)]=!0x0,this[_0xd164e7(0x10c)]=0x0,_0x4b4241))['catch'](_0x52ca7c=>(this[_0xd164e7(0x18a)]=!0x1,this[_0xd164e7(0xc9)]=!0x1,console['warn'](_0xd164e7(0x146)+this[_0xd164e7(0x1aa)]),_0xb44c35(new Error(_0xd164e7(0x10a)+(_0x52ca7c&&_0x52ca7c[_0xd164e7(0x1a0)])))));}));},z[_0x34ea16(0x1a9)][_0x34ea16(0x113)]=function(_0x52ad38){var _0x3a2d63=_0x34ea16;this[_0x3a2d63(0x18a)]=!0x1,this[_0x3a2d63(0xc9)]=!0x1;try{_0x52ad38[_0x3a2d63(0x18b)]=null,_0x52ad38[_0x3a2d63(0x1a7)]=null,_0x52ad38[_0x3a2d63(0x1bd)]=null;}catch{}try{_0x52ad38[_0x3a2d63(0x122)]<0x2&&_0x52ad38[_0x3a2d63(0x108)]();}catch{}},z[_0x34ea16(0x1a9)][_0x34ea16(0xe2)]=function(){var _0x2c48c1=_0x34ea16;clearTimeout(this['_reconnectTimeout']),!(this['_connectAttemptCount']>=this[_0x2c48c1(0xff)])&&(this['_reconnectTimeout']=setTimeout(()=>{var _0x53f711=_0x2c48c1,_0xd9febc;this[_0x53f711(0x18a)]||this[_0x53f711(0xc9)]||(this[_0x53f711(0xdf)](),(_0xd9febc=this[_0x53f711(0x154)])==null||_0xd9febc[_0x53f711(0xf0)](()=>this[_0x53f711(0xe2)]()));},0x1f4),this[_0x2c48c1(0x1a1)][_0x2c48c1(0x14e)]&&this['_reconnectTimeout'][_0x2c48c1(0x14e)]());},z[_0x34ea16(0x1a9)][_0x34ea16(0x199)]=async function(_0x5c9dc9){var _0x19ef95=_0x34ea16;try{if(!this[_0x19ef95(0x155)])return;this[_0x19ef95(0x1c0)]&&this[_0x19ef95(0xdf)](),(await this[_0x19ef95(0x154)])[_0x19ef95(0x199)](JSON['stringify'](_0x5c9dc9));}catch(_0x3d50b3){this[_0x19ef95(0x166)]?console[_0x19ef95(0x1b3)](this[_0x19ef95(0x167)]+':\\x20'+(_0x3d50b3&&_0x3d50b3[_0x19ef95(0x1a0)])):(this[_0x19ef95(0x166)]=!0x0,console[_0x19ef95(0x1b3)](this[_0x19ef95(0x167)]+':\\x20'+(_0x3d50b3&&_0x3d50b3[_0x19ef95(0x1a0)]),_0x5c9dc9)),this[_0x19ef95(0x155)]=!0x1,this[_0x19ef95(0xe2)]();}};function H(_0x1a31ec,_0xf1a76,_0x1c07b4,_0x3f1aa3,_0xe6c0ce,_0x58420c,_0x2165e4,_0x249512=ne){var _0x182527=_0x34ea16;let _0x2ff53f=_0x1c07b4[_0x182527(0x11b)](',')[_0x182527(0x148)](_0x24d8b5=>{var _0x434e83=_0x182527,_0x1f6264,_0x2357f8,_0xc5fa0d,_0x351a9a,_0x5bda54,_0x51f242,_0x344d6f,_0x3741c3;try{if(!_0x1a31ec[_0x434e83(0x124)]){let _0x1150c5=((_0x2357f8=(_0x1f6264=_0x1a31ec[_0x434e83(0x140)])==null?void 0x0:_0x1f6264[_0x434e83(0x1c7)])==null?void 0x0:_0x2357f8[_0x434e83(0x125)])||((_0x351a9a=(_0xc5fa0d=_0x1a31ec[_0x434e83(0x140)])==null?void 0x0:_0xc5fa0d['env'])==null?void 0x0:_0x351a9a[_0x434e83(0xc5)])===_0x434e83(0x15f);(_0xe6c0ce===_0x434e83(0x114)||_0xe6c0ce===_0x434e83(0xd5)||_0xe6c0ce==='astro'||_0xe6c0ce===_0x434e83(0x12f))&&(_0xe6c0ce+=_0x1150c5?_0x434e83(0x191):_0x434e83(0x115));let _0x383226='';_0xe6c0ce===_0x434e83(0xd7)&&(_0x383226=(((_0x344d6f=(_0x51f242=(_0x5bda54=_0x1a31ec['expo'])==null?void 0x0:_0x5bda54[_0x434e83(0x198)])==null?void 0x0:_0x51f242[_0x434e83(0x1bf)])==null?void 0x0:_0x344d6f[_0x434e83(0x16b)])||_0x434e83(0xd2))[_0x434e83(0x17b)](),_0x383226&&(_0xe6c0ce+='\\x20'+_0x383226,(_0x383226==='android'||_0x383226===_0x434e83(0xd2)&&((_0x3741c3=_0x1a31ec['location'])==null?void 0x0:_0x3741c3[_0x434e83(0x143)])==='10.0.2.2')&&(_0xf1a76=_0x434e83(0xe9)))),_0x1a31ec[_0x434e83(0x124)]={'id':+new Date(),'tool':_0xe6c0ce},_0x2165e4&&_0xe6c0ce&&!_0x1150c5&&(_0x383226?console['log']('Console\\x20Ninja\\x20extension\\x20is\\x20connected\\x20to\\x20'+_0x383226+',\\x20see\\x20https://tinyurl.com/2vt8jxzw\\x20for\\x20more\\x20info.'):console['log'](_0x434e83(0x195)+(_0xe6c0ce[_0x434e83(0x185)](0x0)[_0x434e83(0xe0)]()+_0xe6c0ce[_0x434e83(0x1ae)](0x1))+',','background:\\x20rgb(30,30,30);\\x20color:\\x20rgb(255,213,92)',_0x434e83(0xdd)));}let _0x14ac71=new z(_0x1a31ec,_0xf1a76,_0x24d8b5,_0x3f1aa3,_0x58420c,_0x249512);return _0x14ac71[_0x434e83(0x199)]['bind'](_0x14ac71);}catch(_0xe41c30){return console['warn']('logger\\x20failed\\x20to\\x20connect\\x20to\\x20host',_0xe41c30&&_0xe41c30[_0x434e83(0x1a0)]),()=>{};}});return _0x21cc13=>_0x2ff53f['forEach'](_0x4a212f=>_0x4a212f(_0x21cc13));}function ne(_0x32407b,_0x16adef,_0x4c1ebc,_0x322090){var _0x1679a4=_0x34ea16;_0x322090&&_0x32407b===_0x1679a4(0x181)&&_0x4c1ebc[_0x1679a4(0x101)][_0x1679a4(0x181)]();}function b(_0x78765a){var _0x1da627=_0x34ea16,_0x3db069,_0x1e4e29;let _0x2e8ee4=function(_0x30cc21,_0x4c585d){return _0x4c585d-_0x30cc21;},_0x15e1ae;if(_0x78765a['performance'])_0x15e1ae=function(){var _0x1a97c3=_0x397e;return _0x78765a['performance'][_0x1a97c3(0xec)]();};else{if(_0x78765a[_0x1da627(0x140)]&&_0x78765a[_0x1da627(0x140)]['hrtime']&&((_0x1e4e29=(_0x3db069=_0x78765a[_0x1da627(0x140)])==null?void 0x0:_0x3db069['env'])==null?void 0x0:_0x1e4e29['NEXT_RUNTIME'])!==_0x1da627(0x15f))_0x15e1ae=function(){var _0x2e8e8b=_0x1da627;return _0x78765a[_0x2e8e8b(0x140)][_0x2e8e8b(0x1c4)]();},_0x2e8ee4=function(_0x3180f9,_0x1cdf54){return 0x3e8*(_0x1cdf54[0x0]-_0x3180f9[0x0])+(_0x1cdf54[0x1]-_0x3180f9[0x1])/0xf4240;};else try{let {performance:_0x111e4e}=require(_0x1da627(0x169));_0x15e1ae=function(){var _0xce573f=_0x1da627;return _0x111e4e[_0xce573f(0xec)]();};}catch{_0x15e1ae=function(){return+new Date();};}}return{'elapsed':_0x2e8ee4,'timeStamp':_0x15e1ae,'now':()=>Date[_0x1da627(0xec)]()};}function X(_0x3eae27,_0x36e0f8,_0xc25760){var _0x156f86=_0x34ea16,_0x480f84,_0x5209a7,_0xef0065,_0x489147,_0x44d3b0,_0x46d495,_0x502639;if(_0x3eae27[_0x156f86(0x116)]!==void 0x0)return _0x3eae27[_0x156f86(0x116)];let _0x18611f=((_0x5209a7=(_0x480f84=_0x3eae27['process'])==null?void 0x0:_0x480f84[_0x156f86(0x1c7)])==null?void 0x0:_0x5209a7['node'])||((_0x489147=(_0xef0065=_0x3eae27[_0x156f86(0x140)])==null?void 0x0:_0xef0065[_0x156f86(0x142)])==null?void 0x0:_0x489147[_0x156f86(0xc5)])===_0x156f86(0x15f),_0x2895e1=!!(_0xc25760===_0x156f86(0xd7)&&((_0x44d3b0=_0x3eae27['expo'])==null?void 0x0:_0x44d3b0[_0x156f86(0x198)]));function _0x9884bc(_0x4f4c7c){var _0x5452ae=_0x156f86;if(_0x4f4c7c[_0x5452ae(0x175)]('/')&&_0x4f4c7c['endsWith']('/')){let _0x445d00=new RegExp(_0x4f4c7c[_0x5452ae(0x189)](0x1,-0x1));return _0x522d80=>_0x445d00['test'](_0x522d80);}else{if(_0x4f4c7c[_0x5452ae(0x13f)]('*')||_0x4f4c7c[_0x5452ae(0x13f)]('?')){let _0x3bfa3f=new RegExp('^'+_0x4f4c7c[_0x5452ae(0x152)](/\\./g,String['fromCharCode'](0x5c)+'.')['replace'](/\\*/g,'.*')[_0x5452ae(0x152)](/\\?/g,'.')+String['fromCharCode'](0x24));return _0x2965ec=>_0x3bfa3f[_0x5452ae(0x196)](_0x2965ec);}else return _0x19aa76=>_0x19aa76===_0x4f4c7c;}}let _0x385a18=_0x36e0f8[_0x156f86(0x148)](_0x9884bc);return _0x3eae27[_0x156f86(0x116)]=_0x18611f||!_0x36e0f8,!_0x3eae27[_0x156f86(0x116)]&&((_0x46d495=_0x3eae27[_0x156f86(0x101)])==null?void 0x0:_0x46d495[_0x156f86(0x143)])&&(_0x3eae27[_0x156f86(0x116)]=_0x385a18[_0x156f86(0x1a5)](_0x59b31e=>_0x59b31e(_0x3eae27['location'][_0x156f86(0x143)]))),_0x2895e1&&!_0x3eae27['_consoleNinjaAllowedToStart']&&!((_0x502639=_0x3eae27[_0x156f86(0x101)])!=null&&_0x502639[_0x156f86(0x143)])&&(_0x3eae27[_0x156f86(0x116)]=!0x0),_0x3eae27[_0x156f86(0x116)];}function J(_0x4ef3c2,_0x4eb197,_0x470e69,_0xb46878,_0x33b83c,_0x1850b6){var _0x2d3e35=_0x34ea16;_0x4ef3c2=_0x4ef3c2,_0x4eb197=_0x4eb197,_0x470e69=_0x470e69,_0xb46878=_0xb46878,_0x33b83c=_0x33b83c,_0x33b83c=_0x33b83c||{},_0x33b83c[_0x2d3e35(0xdc)]=_0x33b83c[_0x2d3e35(0xdc)]||{},_0x33b83c[_0x2d3e35(0x163)]=_0x33b83c['reducedLimits']||{},_0x33b83c[_0x2d3e35(0x109)]=_0x33b83c['reducePolicy']||{},_0x33b83c['reducePolicy'][_0x2d3e35(0xf3)]=_0x33b83c[_0x2d3e35(0x109)]['perLogpoint']||{},_0x33b83c['reducePolicy'][_0x2d3e35(0x1c3)]=_0x33b83c['reducePolicy'][_0x2d3e35(0x1c3)]||{};let _0x13b741={'perLogpoint':{'reduceOnCount':_0x33b83c[_0x2d3e35(0x109)][_0x2d3e35(0xf3)]['reduceOnCount']||0x32,'reduceOnAccumulatedProcessingTimeMs':_0x33b83c['reducePolicy']['perLogpoint'][_0x2d3e35(0x138)]||0x64,'resetWhenQuietMs':_0x33b83c['reducePolicy'][_0x2d3e35(0xf3)][_0x2d3e35(0x1b1)]||0x1f4,'resetOnProcessingTimeAverageMs':_0x33b83c['reducePolicy']['perLogpoint']['resetOnProcessingTimeAverageMs']||0x64},'global':{'reduceOnCount':_0x33b83c['reducePolicy'][_0x2d3e35(0x1c3)][_0x2d3e35(0x11f)]||0x3e8,'reduceOnAccumulatedProcessingTimeMs':_0x33b83c[_0x2d3e35(0x109)][_0x2d3e35(0x1c3)][_0x2d3e35(0x138)]||0x12c,'resetWhenQuietMs':_0x33b83c[_0x2d3e35(0x109)][_0x2d3e35(0x1c3)][_0x2d3e35(0x1b1)]||0x32,'resetOnProcessingTimeAverageMs':_0x33b83c[_0x2d3e35(0x109)][_0x2d3e35(0x1c3)][_0x2d3e35(0xf5)]||0x64}},_0xed07fb=b(_0x4ef3c2),_0x4120d1=_0xed07fb[_0x2d3e35(0x192)],_0x594fe3=_0xed07fb[_0x2d3e35(0x13e)];function _0x1583f1(){var _0xf32942=_0x2d3e35;this['_keyStrRegExp']=/^(?!(?:do|if|in|for|let|new|try|var|case|else|enum|eval|false|null|this|true|void|with|break|catch|class|const|super|throw|while|yield|delete|export|import|public|return|static|switch|typeof|default|extends|finally|package|private|continue|debugger|function|arguments|interface|protected|implements|instanceof)$)[_$a-zA-Z\\xA0-\\uFFFF][_$a-zA-Z0-9\\xA0-\\uFFFF]*$/,this[_0xf32942(0x117)]=/^(0|[1-9][0-9]*)$/,this[_0xf32942(0x12e)]=/'([^\\\\']|\\\\')*'/,this[_0xf32942(0xd8)]=_0x4ef3c2[_0xf32942(0x164)],this[_0xf32942(0x15c)]=_0x4ef3c2[_0xf32942(0xce)],this[_0xf32942(0x165)]=Object[_0xf32942(0x11e)],this[_0xf32942(0x1ab)]=Object['getOwnPropertyNames'],this[_0xf32942(0x18f)]=_0x4ef3c2['Symbol'],this[_0xf32942(0x17c)]=RegExp[_0xf32942(0x1a9)][_0xf32942(0x176)],this[_0xf32942(0x16c)]=Date[_0xf32942(0x1a9)][_0xf32942(0x176)];}_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x1af)]=function(_0x37d942,_0x1d26f5,_0x4d6a77,_0x41a7e8){var _0x136877=_0x2d3e35,_0x16e8b8=this,_0x1afa4a=_0x4d6a77[_0x136877(0x17e)];function _0x559ba1(_0x192b8a,_0x37d923,_0x579501){var _0x405ab2=_0x136877;_0x37d923[_0x405ab2(0x103)]=_0x405ab2(0x14f),_0x37d923[_0x405ab2(0xf2)]=_0x192b8a['message'],_0x1f9c8f=_0x579501[_0x405ab2(0x125)][_0x405ab2(0x1b8)],_0x579501[_0x405ab2(0x125)]['current']=_0x37d923,_0x16e8b8[_0x405ab2(0xf8)](_0x37d923,_0x579501);}let _0x127081,_0x306ae0,_0x10371b=_0x4ef3c2[_0x136877(0xfe)];_0x4ef3c2[_0x136877(0xfe)]=!0x0,_0x4ef3c2[_0x136877(0xea)]&&(_0x127081=_0x4ef3c2[_0x136877(0xea)][_0x136877(0xf2)],_0x306ae0=_0x4ef3c2[_0x136877(0xea)][_0x136877(0x1b3)],_0x127081&&(_0x4ef3c2[_0x136877(0xea)]['error']=function(){}),_0x306ae0&&(_0x4ef3c2[_0x136877(0xea)][_0x136877(0x1b3)]=function(){}));try{try{_0x4d6a77['level']++,_0x4d6a77[_0x136877(0x17e)]&&_0x4d6a77[_0x136877(0xc2)][_0x136877(0xe6)](_0x1d26f5);var _0x50332b,_0x87d93e,_0x173e43,_0x52beed,_0x4ec178=[],_0x489f5a=[],_0x3fee05,_0x4770cb=this[_0x136877(0x156)](_0x1d26f5),_0x381294=_0x4770cb==='array',_0x79c071=!0x1,_0x12fdeb=_0x4770cb===_0x136877(0xc1),_0x2bffa7=this[_0x136877(0xda)](_0x4770cb),_0x5e08fc=this['_isPrimitiveWrapperType'](_0x4770cb),_0x3a5294=_0x2bffa7||_0x5e08fc,_0x192220={},_0x2e7977=0x0,_0x10a7bb=!0x1,_0x1f9c8f,_0x40d3a3=/^(([1-9]{1}[0-9]*)|0)$/;if(_0x4d6a77['depth']){if(_0x381294){if(_0x87d93e=_0x1d26f5[_0x136877(0xee)],_0x87d93e>_0x4d6a77[_0x136877(0x10e)]){for(_0x173e43=0x0,_0x52beed=_0x4d6a77[_0x136877(0x10e)],_0x50332b=_0x173e43;_0x50332b<_0x52beed;_0x50332b++)_0x489f5a['push'](_0x16e8b8['_addProperty'](_0x4ec178,_0x1d26f5,_0x4770cb,_0x50332b,_0x4d6a77));_0x37d942[_0x136877(0xef)]=!0x0;}else{for(_0x173e43=0x0,_0x52beed=_0x87d93e,_0x50332b=_0x173e43;_0x50332b<_0x52beed;_0x50332b++)_0x489f5a[_0x136877(0xe6)](_0x16e8b8[_0x136877(0x1c5)](_0x4ec178,_0x1d26f5,_0x4770cb,_0x50332b,_0x4d6a77));}_0x4d6a77['autoExpandPropertyCount']+=_0x489f5a['length'];}if(!(_0x4770cb===_0x136877(0x136)||_0x4770cb===_0x136877(0x164))&&!_0x2bffa7&&_0x4770cb!==_0x136877(0x157)&&_0x4770cb!=='Buffer'&&_0x4770cb!==_0x136877(0x19b)){var _0x5b0375=_0x41a7e8[_0x136877(0x180)]||_0x4d6a77[_0x136877(0x180)];if(this[_0x136877(0xe1)](_0x1d26f5)?(_0x50332b=0x0,_0x1d26f5[_0x136877(0x18c)](function(_0x1f0fb1){var _0x36d9f2=_0x136877;if(_0x2e7977++,_0x4d6a77['autoExpandPropertyCount']++,_0x2e7977>_0x5b0375){_0x10a7bb=!0x0;return;}if(!_0x4d6a77[_0x36d9f2(0xc6)]&&_0x4d6a77[_0x36d9f2(0x17e)]&&_0x4d6a77[_0x36d9f2(0x12a)]>_0x4d6a77[_0x36d9f2(0x1c2)]){_0x10a7bb=!0x0;return;}_0x489f5a['push'](_0x16e8b8['_addProperty'](_0x4ec178,_0x1d26f5,_0x36d9f2(0x13c),_0x50332b++,_0x4d6a77,function(_0x3c31e8){return function(){return _0x3c31e8;};}(_0x1f0fb1)));})):this[_0x136877(0x178)](_0x1d26f5)&&_0x1d26f5[_0x136877(0x18c)](function(_0x142bff,_0x47c78f){var _0x3c93fb=_0x136877;if(_0x2e7977++,_0x4d6a77['autoExpandPropertyCount']++,_0x2e7977>_0x5b0375){_0x10a7bb=!0x0;return;}if(!_0x4d6a77[_0x3c93fb(0xc6)]&&_0x4d6a77[_0x3c93fb(0x17e)]&&_0x4d6a77[_0x3c93fb(0x12a)]>_0x4d6a77[_0x3c93fb(0x1c2)]){_0x10a7bb=!0x0;return;}var _0x331ed7=_0x47c78f[_0x3c93fb(0x176)]();_0x331ed7['length']>0x64&&(_0x331ed7=_0x331ed7[_0x3c93fb(0x189)](0x0,0x64)+'...'),_0x489f5a['push'](_0x16e8b8[_0x3c93fb(0x1c5)](_0x4ec178,_0x1d26f5,_0x3c93fb(0x158),_0x331ed7,_0x4d6a77,function(_0x3f00e2){return function(){return _0x3f00e2;};}(_0x142bff)));}),!_0x79c071){try{for(_0x3fee05 in _0x1d26f5)if(!(_0x381294&&_0x40d3a3[_0x136877(0x196)](_0x3fee05))&&!this[_0x136877(0x1b5)](_0x1d26f5,_0x3fee05,_0x4d6a77)){if(_0x2e7977++,_0x4d6a77[_0x136877(0x12a)]++,_0x2e7977>_0x5b0375){_0x10a7bb=!0x0;break;}if(!_0x4d6a77['isExpressionToEvaluate']&&_0x4d6a77[_0x136877(0x17e)]&&_0x4d6a77[_0x136877(0x12a)]>_0x4d6a77[_0x136877(0x1c2)]){_0x10a7bb=!0x0;break;}_0x489f5a[_0x136877(0xe6)](_0x16e8b8[_0x136877(0x119)](_0x4ec178,_0x192220,_0x1d26f5,_0x4770cb,_0x3fee05,_0x4d6a77));}}catch{}if(_0x192220[_0x136877(0xe8)]=!0x0,_0x12fdeb&&(_0x192220[_0x136877(0x16a)]=!0x0),!_0x10a7bb){var _0x5b13a1=[][_0x136877(0x159)](this[_0x136877(0x1ab)](_0x1d26f5))['concat'](this[_0x136877(0xc4)](_0x1d26f5));for(_0x50332b=0x0,_0x87d93e=_0x5b13a1['length'];_0x50332b<_0x87d93e;_0x50332b++)if(_0x3fee05=_0x5b13a1[_0x50332b],!(_0x381294&&_0x40d3a3[_0x136877(0x196)](_0x3fee05[_0x136877(0x176)]()))&&!this[_0x136877(0x1b5)](_0x1d26f5,_0x3fee05,_0x4d6a77)&&!_0x192220[typeof _0x3fee05!=_0x136877(0xed)?_0x136877(0xd9)+_0x3fee05[_0x136877(0x176)]():_0x3fee05]){if(_0x2e7977++,_0x4d6a77['autoExpandPropertyCount']++,_0x2e7977>_0x5b0375){_0x10a7bb=!0x0;break;}if(!_0x4d6a77['isExpressionToEvaluate']&&_0x4d6a77['autoExpand']&&_0x4d6a77[_0x136877(0x12a)]>_0x4d6a77[_0x136877(0x1c2)]){_0x10a7bb=!0x0;break;}_0x489f5a['push'](_0x16e8b8[_0x136877(0x119)](_0x4ec178,_0x192220,_0x1d26f5,_0x4770cb,_0x3fee05,_0x4d6a77));}}}}}if(_0x37d942[_0x136877(0x103)]=_0x4770cb,_0x3a5294?(_0x37d942[_0x136877(0x14b)]=_0x1d26f5[_0x136877(0x151)](),this[_0x136877(0x1c6)](_0x4770cb,_0x37d942,_0x4d6a77,_0x41a7e8)):_0x4770cb===_0x136877(0x13d)?_0x37d942[_0x136877(0x14b)]=this['_dateToString']['call'](_0x1d26f5):_0x4770cb==='bigint'?_0x37d942[_0x136877(0x14b)]=_0x1d26f5[_0x136877(0x176)]():_0x4770cb===_0x136877(0x18d)?_0x37d942[_0x136877(0x14b)]=this[_0x136877(0x17c)]['call'](_0x1d26f5):_0x4770cb===_0x136877(0xed)&&this[_0x136877(0x18f)]?_0x37d942[_0x136877(0x14b)]=this[_0x136877(0x18f)][_0x136877(0x1a9)][_0x136877(0x176)][_0x136877(0x162)](_0x1d26f5):!_0x4d6a77[_0x136877(0xc0)]&&!(_0x4770cb===_0x136877(0x136)||_0x4770cb===_0x136877(0x164))&&(delete _0x37d942[_0x136877(0x14b)],_0x37d942[_0x136877(0xe3)]=!0x0),_0x10a7bb&&(_0x37d942[_0x136877(0x184)]=!0x0),_0x1f9c8f=_0x4d6a77['node'][_0x136877(0x1b8)],_0x4d6a77[_0x136877(0x125)]['current']=_0x37d942,this['_treeNodePropertiesBeforeFullValue'](_0x37d942,_0x4d6a77),_0x489f5a[_0x136877(0xee)]){for(_0x50332b=0x0,_0x87d93e=_0x489f5a['length'];_0x50332b<_0x87d93e;_0x50332b++)_0x489f5a[_0x50332b](_0x50332b);}_0x4ec178['length']&&(_0x37d942[_0x136877(0x180)]=_0x4ec178);}catch(_0x1ba7c5){_0x559ba1(_0x1ba7c5,_0x37d942,_0x4d6a77);}this[_0x136877(0xc7)](_0x1d26f5,_0x37d942),this[_0x136877(0x14d)](_0x37d942,_0x4d6a77),_0x4d6a77[_0x136877(0x125)]['current']=_0x1f9c8f,_0x4d6a77[_0x136877(0x187)]--,_0x4d6a77[_0x136877(0x17e)]=_0x1afa4a,_0x4d6a77['autoExpand']&&_0x4d6a77[_0x136877(0xc2)][_0x136877(0x137)]();}finally{_0x127081&&(_0x4ef3c2[_0x136877(0xea)][_0x136877(0xf2)]=_0x127081),_0x306ae0&&(_0x4ef3c2[_0x136877(0xea)][_0x136877(0x1b3)]=_0x306ae0),_0x4ef3c2[_0x136877(0xfe)]=_0x10371b;}return _0x37d942;},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0xc4)]=function(_0x53499a){var _0x15da05=_0x2d3e35;return Object[_0x15da05(0x1ad)]?Object['getOwnPropertySymbols'](_0x53499a):[];},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0xe1)]=function(_0x327333){var _0x363956=_0x2d3e35;return!!(_0x327333&&_0x4ef3c2[_0x363956(0x13c)]&&this['_objectToString'](_0x327333)===_0x363956(0x150)&&_0x327333[_0x363956(0x18c)]);},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x1b5)]=function(_0x4d706b,_0x44b009,_0x55e35b){var _0x210c88=_0x2d3e35;if(!_0x55e35b['resolveGetters']){let _0x1fc869=this[_0x210c88(0x165)](_0x4d706b,_0x44b009);if(_0x1fc869&&_0x1fc869[_0x210c88(0x171)])return!0x0;}return _0x55e35b[_0x210c88(0x13a)]?typeof _0x4d706b[_0x44b009]=='function':!0x1;},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x156)]=function(_0x1be33b){var _0x1ce0bf=_0x2d3e35,_0x23767c='';return _0x23767c=typeof _0x1be33b,_0x23767c===_0x1ce0bf(0x190)?this[_0x1ce0bf(0xd0)](_0x1be33b)==='[object\\x20Array]'?_0x23767c='array':this[_0x1ce0bf(0xd0)](_0x1be33b)===_0x1ce0bf(0x10f)?_0x23767c=_0x1ce0bf(0x13d):this[_0x1ce0bf(0xd0)](_0x1be33b)==='[object\\x20BigInt]'?_0x23767c='bigint':_0x1be33b===null?_0x23767c=_0x1ce0bf(0x136):_0x1be33b['constructor']&&(_0x23767c=_0x1be33b['constructor'][_0x1ce0bf(0xc8)]||_0x23767c):_0x23767c==='undefined'&&this[_0x1ce0bf(0x15c)]&&_0x1be33b instanceof this[_0x1ce0bf(0x15c)]&&(_0x23767c='HTMLAllCollection'),_0x23767c;},_0x1583f1[_0x2d3e35(0x1a9)]['_objectToString']=function(_0x354fe6){var _0x44128c=_0x2d3e35;return Object[_0x44128c(0x1a9)][_0x44128c(0x176)][_0x44128c(0x162)](_0x354fe6);},_0x1583f1['prototype'][_0x2d3e35(0xda)]=function(_0x3d1ae4){var _0xbd4748=_0x2d3e35;return _0x3d1ae4===_0xbd4748(0x120)||_0x3d1ae4===_0xbd4748(0xf7)||_0x3d1ae4==='number';},_0x1583f1['prototype'][_0x2d3e35(0xd3)]=function(_0x412adf){var _0x29bd86=_0x2d3e35;return _0x412adf==='Boolean'||_0x412adf===_0x29bd86(0x157)||_0x412adf===_0x29bd86(0x13b);},_0x1583f1[_0x2d3e35(0x1a9)]['_addProperty']=function(_0x2079bd,_0x4c4a98,_0x3cda04,_0x5218de,_0x270678,_0x32fef1){var _0x100c4c=this;return function(_0x2eeb60){var _0x24e956=_0x397e,_0x16d835=_0x270678[_0x24e956(0x125)][_0x24e956(0x1b8)],_0x47fa7a=_0x270678['node'][_0x24e956(0x16f)],_0x283ae8=_0x270678[_0x24e956(0x125)][_0x24e956(0x126)];_0x270678[_0x24e956(0x125)][_0x24e956(0x126)]=_0x16d835,_0x270678['node']['index']=typeof _0x5218de==_0x24e956(0x149)?_0x5218de:_0x2eeb60,_0x2079bd[_0x24e956(0xe6)](_0x100c4c[_0x24e956(0x147)](_0x4c4a98,_0x3cda04,_0x5218de,_0x270678,_0x32fef1)),_0x270678['node']['parent']=_0x283ae8,_0x270678['node'][_0x24e956(0x16f)]=_0x47fa7a;};},_0x1583f1['prototype'][_0x2d3e35(0x119)]=function(_0x3dfa6e,_0x29f5b9,_0x39e96a,_0x182f0e,_0x131285,_0x10589a,_0xcac687){var _0x31ac0d=_0x2d3e35,_0x56994f=this;return _0x29f5b9[typeof _0x131285!=_0x31ac0d(0xed)?_0x31ac0d(0xd9)+_0x131285[_0x31ac0d(0x176)]():_0x131285]=!0x0,function(_0x1de9f9){var _0xaeb905=_0x31ac0d,_0x7686e8=_0x10589a[_0xaeb905(0x125)][_0xaeb905(0x1b8)],_0x266d8e=_0x10589a[_0xaeb905(0x125)][_0xaeb905(0x16f)],_0xec6ea8=_0x10589a['node'][_0xaeb905(0x126)];_0x10589a['node'][_0xaeb905(0x126)]=_0x7686e8,_0x10589a[_0xaeb905(0x125)]['index']=_0x1de9f9,_0x3dfa6e['push'](_0x56994f['_property'](_0x39e96a,_0x182f0e,_0x131285,_0x10589a,_0xcac687)),_0x10589a[_0xaeb905(0x125)][_0xaeb905(0x126)]=_0xec6ea8,_0x10589a[_0xaeb905(0x125)][_0xaeb905(0x16f)]=_0x266d8e;};},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x147)]=function(_0x106c3b,_0x3521d4,_0x105c34,_0x29c8c1,_0x5b186f){var _0x3fce09=_0x2d3e35,_0x434381=this;_0x5b186f||(_0x5b186f=function(_0x42b7e9,_0x38cf7e){return _0x42b7e9[_0x38cf7e];});var _0x56afac=_0x105c34[_0x3fce09(0x176)](),_0x54da9a=_0x29c8c1[_0x3fce09(0x102)]||{},_0xd88c81=_0x29c8c1[_0x3fce09(0xc0)],_0x4bd0f8=_0x29c8c1['isExpressionToEvaluate'];try{var _0x459dc9=this[_0x3fce09(0x178)](_0x106c3b),_0x1ed3a9=_0x56afac;_0x459dc9&&_0x1ed3a9[0x0]==='\\x27'&&(_0x1ed3a9=_0x1ed3a9[_0x3fce09(0x1ae)](0x1,_0x1ed3a9[_0x3fce09(0xee)]-0x2));var _0x9a0801=_0x29c8c1[_0x3fce09(0x102)]=_0x54da9a[_0x3fce09(0xd9)+_0x1ed3a9];_0x9a0801&&(_0x29c8c1[_0x3fce09(0xc0)]=_0x29c8c1[_0x3fce09(0xc0)]+0x1),_0x29c8c1['isExpressionToEvaluate']=!!_0x9a0801;var _0x4e90fc=typeof _0x105c34==_0x3fce09(0xed),_0x5a3dc4={'name':_0x4e90fc||_0x459dc9?_0x56afac:this[_0x3fce09(0xf4)](_0x56afac)};if(_0x4e90fc&&(_0x5a3dc4[_0x3fce09(0xed)]=!0x0),!(_0x3521d4===_0x3fce09(0x1a6)||_0x3521d4==='Error')){var _0x516fff=this[_0x3fce09(0x165)](_0x106c3b,_0x105c34);if(_0x516fff&&(_0x516fff['set']&&(_0x5a3dc4[_0x3fce09(0x194)]=!0x0),_0x516fff[_0x3fce09(0x171)]&&!_0x9a0801&&!_0x29c8c1[_0x3fce09(0x177)]))return _0x5a3dc4[_0x3fce09(0xe4)]=!0x0,this[_0x3fce09(0x139)](_0x5a3dc4,_0x29c8c1),_0x5a3dc4;}var _0x3652c9;try{_0x3652c9=_0x5b186f(_0x106c3b,_0x105c34);}catch(_0x22ea8e){return _0x5a3dc4={'name':_0x56afac,'type':_0x3fce09(0x14f),'error':_0x22ea8e[_0x3fce09(0x1a0)]},this['_processTreeNodeResult'](_0x5a3dc4,_0x29c8c1),_0x5a3dc4;}var _0x3aa073=this[_0x3fce09(0x156)](_0x3652c9),_0x4d7396=this[_0x3fce09(0xda)](_0x3aa073);if(_0x5a3dc4[_0x3fce09(0x103)]=_0x3aa073,_0x4d7396)this[_0x3fce09(0x139)](_0x5a3dc4,_0x29c8c1,_0x3652c9,function(){var _0x3c78fd=_0x3fce09;_0x5a3dc4[_0x3c78fd(0x14b)]=_0x3652c9[_0x3c78fd(0x151)](),!_0x9a0801&&_0x434381[_0x3c78fd(0x1c6)](_0x3aa073,_0x5a3dc4,_0x29c8c1,{});});else{var _0x43bccc=_0x29c8c1['autoExpand']&&_0x29c8c1[_0x3fce09(0x187)]<_0x29c8c1[_0x3fce09(0xeb)]&&_0x29c8c1['autoExpandPreviousObjects'][_0x3fce09(0x19a)](_0x3652c9)<0x0&&_0x3aa073!==_0x3fce09(0xc1)&&_0x29c8c1[_0x3fce09(0x12a)]<_0x29c8c1[_0x3fce09(0x1c2)];_0x43bccc||_0x29c8c1['level']<_0xd88c81||_0x9a0801?this[_0x3fce09(0x1af)](_0x5a3dc4,_0x3652c9,_0x29c8c1,_0x9a0801||{}):this['_processTreeNodeResult'](_0x5a3dc4,_0x29c8c1,_0x3652c9,function(){var _0x561fdc=_0x3fce09;_0x3aa073===_0x561fdc(0x136)||_0x3aa073===_0x561fdc(0x164)||(delete _0x5a3dc4['value'],_0x5a3dc4[_0x561fdc(0xe3)]=!0x0);});}return _0x5a3dc4;}finally{_0x29c8c1['expressionsToEvaluate']=_0x54da9a,_0x29c8c1['depth']=_0xd88c81,_0x29c8c1[_0x3fce09(0xc6)]=_0x4bd0f8;}},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x1c6)]=function(_0x1f19c1,_0x3fe20e,_0x5a4406,_0x6752d5){var _0x2315ed=_0x2d3e35,_0x3b9e77=_0x6752d5['strLength']||_0x5a4406[_0x2315ed(0x110)];if((_0x1f19c1===_0x2315ed(0xf7)||_0x1f19c1===_0x2315ed(0x157))&&_0x3fe20e[_0x2315ed(0x14b)]){let _0x1be3d9=_0x3fe20e[_0x2315ed(0x14b)][_0x2315ed(0xee)];_0x5a4406[_0x2315ed(0x121)]+=_0x1be3d9,_0x5a4406[_0x2315ed(0x121)]>_0x5a4406['totalStrLength']?(_0x3fe20e[_0x2315ed(0xe3)]='',delete _0x3fe20e[_0x2315ed(0x14b)]):_0x1be3d9>_0x3b9e77&&(_0x3fe20e[_0x2315ed(0xe3)]=_0x3fe20e[_0x2315ed(0x14b)][_0x2315ed(0x1ae)](0x0,_0x3b9e77),delete _0x3fe20e['value']);}},_0x1583f1['prototype'][_0x2d3e35(0x178)]=function(_0x36ba81){var _0x23d5f7=_0x2d3e35;return!!(_0x36ba81&&_0x4ef3c2[_0x23d5f7(0x158)]&&this[_0x23d5f7(0xd0)](_0x36ba81)===_0x23d5f7(0x100)&&_0x36ba81[_0x23d5f7(0x18c)]);},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0xf4)]=function(_0x7599c7){var _0x278d14=_0x2d3e35;if(_0x7599c7[_0x278d14(0x12d)](/^\\d+$/))return _0x7599c7;var _0x130d11;try{_0x130d11=JSON[_0x278d14(0x135)](''+_0x7599c7);}catch{_0x130d11='\\x22'+this['_objectToString'](_0x7599c7)+'\\x22';}return _0x130d11[_0x278d14(0x12d)](/^\"([a-zA-Z_][a-zA-Z_0-9]*)\"$/)?_0x130d11=_0x130d11[_0x278d14(0x1ae)](0x1,_0x130d11['length']-0x2):_0x130d11=_0x130d11[_0x278d14(0x152)](/'/g,'\\x5c\\x27')['replace'](/\\\\\"/g,'\\x22')['replace'](/(^\"|\"$)/g,'\\x27'),_0x130d11;},_0x1583f1[_0x2d3e35(0x1a9)]['_processTreeNodeResult']=function(_0x1e17c6,_0x4dbf7a,_0x55968b,_0x4d38c){var _0x5838cb=_0x2d3e35;this[_0x5838cb(0xf8)](_0x1e17c6,_0x4dbf7a),_0x4d38c&&_0x4d38c(),this[_0x5838cb(0xc7)](_0x55968b,_0x1e17c6),this['_treeNodePropertiesAfterFullValue'](_0x1e17c6,_0x4dbf7a);},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0xf8)]=function(_0x749f4b,_0x77ab32){var _0x6f4df7=_0x2d3e35;this[_0x6f4df7(0x153)](_0x749f4b,_0x77ab32),this['_setNodeQueryPath'](_0x749f4b,_0x77ab32),this[_0x6f4df7(0x10d)](_0x749f4b,_0x77ab32),this['_setNodePermissions'](_0x749f4b,_0x77ab32);},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x153)]=function(_0x521424,_0x45325e){},_0x1583f1['prototype'][_0x2d3e35(0x1b7)]=function(_0x65a88e,_0x2f87ce){},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x179)]=function(_0x3cc042,_0x145e0d){},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x17d)]=function(_0xe3db36){return _0xe3db36===this['_undefined'];},_0x1583f1[_0x2d3e35(0x1a9)]['_treeNodePropertiesAfterFullValue']=function(_0x280c79,_0x38fd98){var _0x1a82b0=_0x2d3e35;this[_0x1a82b0(0x179)](_0x280c79,_0x38fd98),this[_0x1a82b0(0x1bc)](_0x280c79),_0x38fd98['sortProps']&&this['_sortProps'](_0x280c79),this['_addFunctionsNode'](_0x280c79,_0x38fd98),this[_0x1a82b0(0x144)](_0x280c79,_0x38fd98),this['_cleanNode'](_0x280c79);},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0xc7)]=function(_0x26d9a5,_0x2b1786){var _0x589a97=_0x2d3e35;try{_0x26d9a5&&typeof _0x26d9a5['length']==_0x589a97(0x149)&&(_0x2b1786[_0x589a97(0xee)]=_0x26d9a5[_0x589a97(0xee)]);}catch{}if(_0x2b1786[_0x589a97(0x103)]===_0x589a97(0x149)||_0x2b1786['type']==='Number'){if(isNaN(_0x2b1786['value']))_0x2b1786[_0x589a97(0x131)]=!0x0,delete _0x2b1786[_0x589a97(0x14b)];else switch(_0x2b1786[_0x589a97(0x14b)]){case Number[_0x589a97(0x123)]:_0x2b1786[_0x589a97(0x193)]=!0x0,delete _0x2b1786[_0x589a97(0x14b)];break;case Number[_0x589a97(0xfa)]:_0x2b1786[_0x589a97(0xca)]=!0x0,delete _0x2b1786[_0x589a97(0x14b)];break;case 0x0:this[_0x589a97(0xcf)](_0x2b1786[_0x589a97(0x14b)])&&(_0x2b1786[_0x589a97(0xbf)]=!0x0);break;}}else _0x2b1786[_0x589a97(0x103)]===_0x589a97(0xc1)&&typeof _0x26d9a5['name']==_0x589a97(0xf7)&&_0x26d9a5['name']&&_0x2b1786[_0x589a97(0xc8)]&&_0x26d9a5[_0x589a97(0xc8)]!==_0x2b1786[_0x589a97(0xc8)]&&(_0x2b1786[_0x589a97(0x1be)]=_0x26d9a5['name']);},_0x1583f1['prototype'][_0x2d3e35(0xcf)]=function(_0xaf9090){var _0x2180fd=_0x2d3e35;return 0x1/_0xaf9090===Number[_0x2180fd(0xfa)];},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x1a2)]=function(_0x4be9f1){var _0x3573aa=_0x2d3e35;!_0x4be9f1[_0x3573aa(0x180)]||!_0x4be9f1[_0x3573aa(0x180)][_0x3573aa(0xee)]||_0x4be9f1[_0x3573aa(0x103)]===_0x3573aa(0x1a6)||_0x4be9f1[_0x3573aa(0x103)]===_0x3573aa(0x158)||_0x4be9f1[_0x3573aa(0x103)]===_0x3573aa(0x13c)||_0x4be9f1[_0x3573aa(0x180)]['sort'](function(_0x4088c4,_0x272e5e){var _0x34339b=_0x3573aa,_0x4deb33=_0x4088c4[_0x34339b(0xc8)][_0x34339b(0x17b)](),_0x2d4fcd=_0x272e5e[_0x34339b(0xc8)][_0x34339b(0x17b)]();return _0x4deb33<_0x2d4fcd?-0x1:_0x4deb33>_0x2d4fcd?0x1:0x0;});},_0x1583f1['prototype'][_0x2d3e35(0x1bb)]=function(_0x7c682c,_0xc5d35e){var _0x1da479=_0x2d3e35;if(!(_0xc5d35e[_0x1da479(0x13a)]||!_0x7c682c[_0x1da479(0x180)]||!_0x7c682c[_0x1da479(0x180)][_0x1da479(0xee)])){for(var _0x51abfa=[],_0x34e082=[],_0x2ebf16=0x0,_0x19caa3=_0x7c682c[_0x1da479(0x180)][_0x1da479(0xee)];_0x2ebf16<_0x19caa3;_0x2ebf16++){var _0x2cb464=_0x7c682c[_0x1da479(0x180)][_0x2ebf16];_0x2cb464[_0x1da479(0x103)]===_0x1da479(0xc1)?_0x51abfa[_0x1da479(0xe6)](_0x2cb464):_0x34e082['push'](_0x2cb464);}if(!(!_0x34e082[_0x1da479(0xee)]||_0x51abfa['length']<=0x1)){_0x7c682c['props']=_0x34e082;var _0x58a293={'functionsNode':!0x0,'props':_0x51abfa};this[_0x1da479(0x153)](_0x58a293,_0xc5d35e),this[_0x1da479(0x179)](_0x58a293,_0xc5d35e),this[_0x1da479(0x1bc)](_0x58a293),this[_0x1da479(0x17a)](_0x58a293,_0xc5d35e),_0x58a293['id']+='\\x20f',_0x7c682c['props'][_0x1da479(0x12b)](_0x58a293);}}},_0x1583f1[_0x2d3e35(0x1a9)]['_addLoadNode']=function(_0x57dac9,_0x503e93){},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x1bc)]=function(_0x5cfa22){},_0x1583f1[_0x2d3e35(0x1a9)]['_isArray']=function(_0x3ae020){var _0xea91ce=_0x2d3e35;return Array[_0xea91ce(0x183)](_0x3ae020)||typeof _0x3ae020=='object'&&this[_0xea91ce(0xd0)](_0x3ae020)===_0xea91ce(0x107);},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x17a)]=function(_0x4bd386,_0x39eb82){},_0x1583f1['prototype'][_0x2d3e35(0xc3)]=function(_0x3ee547){var _0x18d945=_0x2d3e35;delete _0x3ee547[_0x18d945(0x112)],delete _0x3ee547[_0x18d945(0x197)],delete _0x3ee547['_hasMapOnItsPath'];},_0x1583f1[_0x2d3e35(0x1a9)][_0x2d3e35(0x10d)]=function(_0x4ae76c,_0x3614a7){};let _0x1cd003=new _0x1583f1(),_0x35f2a4={'props':_0x33b83c['defaultLimits'][_0x2d3e35(0x180)]||0x64,'elements':_0x33b83c[_0x2d3e35(0xdc)][_0x2d3e35(0x10e)]||0x64,'strLength':_0x33b83c[_0x2d3e35(0xdc)][_0x2d3e35(0x110)]||0x400*0x32,'totalStrLength':_0x33b83c['defaultLimits'][_0x2d3e35(0x11c)]||0x400*0x32,'autoExpandLimit':_0x33b83c[_0x2d3e35(0xdc)][_0x2d3e35(0x1c2)]||0x1388,'autoExpandMaxDepth':_0x33b83c['defaultLimits']['autoExpandMaxDepth']||0xa},_0x4f2d54={'props':_0x33b83c['reducedLimits'][_0x2d3e35(0x180)]||0x5,'elements':_0x33b83c[_0x2d3e35(0x163)][_0x2d3e35(0x10e)]||0x5,'strLength':_0x33b83c[_0x2d3e35(0x163)][_0x2d3e35(0x110)]||0x100,'totalStrLength':_0x33b83c[_0x2d3e35(0x163)][_0x2d3e35(0x11c)]||0x100*0x3,'autoExpandLimit':_0x33b83c[_0x2d3e35(0x163)][_0x2d3e35(0x1c2)]||0x1e,'autoExpandMaxDepth':_0x33b83c[_0x2d3e35(0x163)][_0x2d3e35(0xeb)]||0x2};if(_0x1850b6){let _0x1ed37f=_0x1cd003[_0x2d3e35(0x1af)][_0x2d3e35(0x16d)](_0x1cd003);_0x1cd003['serialize']=function(_0x20b9e3,_0xde4523,_0x6dfb6e,_0x45b9d8){return _0x1ed37f(_0x20b9e3,_0x1850b6(_0xde4523),_0x6dfb6e,_0x45b9d8);};}function _0x853c2d(_0x19de88,_0x92ffd3,_0x1d67ec,_0x3c0439,_0x2c965c,_0x5f0da7){var _0x91f555=_0x2d3e35;let _0x28659b,_0x1bec47;try{_0x1bec47=_0x594fe3(),_0x28659b=_0x470e69[_0x92ffd3],!_0x28659b||_0x1bec47-_0x28659b['ts']>_0x13b741[_0x91f555(0xf3)]['resetWhenQuietMs']&&_0x28659b['count']&&_0x28659b[_0x91f555(0x145)]/_0x28659b[_0x91f555(0x1ac)]<_0x13b741[_0x91f555(0xf3)][_0x91f555(0xf5)]?(_0x470e69[_0x92ffd3]=_0x28659b={'count':0x0,'time':0x0,'ts':_0x1bec47},_0x470e69['hits']={}):_0x1bec47-_0x470e69['hits']['ts']>_0x13b741[_0x91f555(0x1c3)][_0x91f555(0x1b1)]&&_0x470e69[_0x91f555(0xd1)]['count']&&_0x470e69[_0x91f555(0xd1)][_0x91f555(0x145)]/_0x470e69['hits']['count']<_0x13b741[_0x91f555(0x1c3)]['resetOnProcessingTimeAverageMs']&&(_0x470e69[_0x91f555(0xd1)]={});let _0x4b3ded=[],_0x506fb2=_0x28659b['reduceLimits']||_0x470e69[_0x91f555(0xd1)][_0x91f555(0x105)]?_0x4f2d54:_0x35f2a4,_0x48c9a7=_0x484e73=>{var _0x3afe8d=_0x91f555;let _0x21d1e7={};return _0x21d1e7[_0x3afe8d(0x180)]=_0x484e73[_0x3afe8d(0x180)],_0x21d1e7[_0x3afe8d(0x10e)]=_0x484e73[_0x3afe8d(0x10e)],_0x21d1e7['strLength']=_0x484e73[_0x3afe8d(0x110)],_0x21d1e7[_0x3afe8d(0x11c)]=_0x484e73[_0x3afe8d(0x11c)],_0x21d1e7[_0x3afe8d(0x1c2)]=_0x484e73['autoExpandLimit'],_0x21d1e7[_0x3afe8d(0xeb)]=_0x484e73[_0x3afe8d(0xeb)],_0x21d1e7[_0x3afe8d(0xd4)]=!0x1,_0x21d1e7['noFunctions']=!_0x4eb197,_0x21d1e7['depth']=0x1,_0x21d1e7['level']=0x0,_0x21d1e7[_0x3afe8d(0x16e)]=_0x3afe8d(0x104),_0x21d1e7[_0x3afe8d(0x186)]='root_exp',_0x21d1e7[_0x3afe8d(0x17e)]=!0x0,_0x21d1e7[_0x3afe8d(0xc2)]=[],_0x21d1e7[_0x3afe8d(0x12a)]=0x0,_0x21d1e7[_0x3afe8d(0x177)]=_0x33b83c['resolveGetters'],_0x21d1e7[_0x3afe8d(0x121)]=0x0,_0x21d1e7[_0x3afe8d(0x125)]={'current':void 0x0,'parent':void 0x0,'index':0x0},_0x21d1e7;};for(var _0x4e686f=0x0;_0x4e686f<_0x2c965c['length'];_0x4e686f++)_0x4b3ded[_0x91f555(0xe6)](_0x1cd003[_0x91f555(0x1af)]({'timeNode':_0x19de88===_0x91f555(0x145)||void 0x0},_0x2c965c[_0x4e686f],_0x48c9a7(_0x506fb2),{}));if(_0x19de88===_0x91f555(0x182)||_0x19de88==='error'){let _0x5e46b1=Error[_0x91f555(0x1b9)];try{Error[_0x91f555(0x1b9)]=0x1/0x0,_0x4b3ded['push'](_0x1cd003['serialize']({'stackNode':!0x0},new Error()[_0x91f555(0x10b)],_0x48c9a7(_0x506fb2),{'strLength':0x1/0x0}));}finally{Error[_0x91f555(0x1b9)]=_0x5e46b1;}}return{'method':'log','version':_0xb46878,'args':[{'ts':_0x1d67ec,'session':_0x3c0439,'args':_0x4b3ded,'id':_0x92ffd3,'context':_0x5f0da7}]};}catch(_0x51d222){return{'method':_0x91f555(0x172),'version':_0xb46878,'args':[{'ts':_0x1d67ec,'session':_0x3c0439,'args':[{'type':_0x91f555(0x14f),'error':_0x51d222&&_0x51d222[_0x91f555(0x1a0)]}],'id':_0x92ffd3,'context':_0x5f0da7}]};}finally{try{if(_0x28659b&&_0x1bec47){let _0x3f4e88=_0x594fe3();_0x28659b[_0x91f555(0x1ac)]++,_0x28659b[_0x91f555(0x145)]+=_0x4120d1(_0x1bec47,_0x3f4e88),_0x28659b['ts']=_0x3f4e88,_0x470e69['hits'][_0x91f555(0x1ac)]++,_0x470e69[_0x91f555(0xd1)][_0x91f555(0x145)]+=_0x4120d1(_0x1bec47,_0x3f4e88),_0x470e69['hits']['ts']=_0x3f4e88,(_0x28659b[_0x91f555(0x1ac)]>_0x13b741[_0x91f555(0xf3)][_0x91f555(0x11f)]||_0x28659b[_0x91f555(0x145)]>_0x13b741[_0x91f555(0xf3)][_0x91f555(0x138)])&&(_0x28659b[_0x91f555(0x105)]=!0x0),(_0x470e69[_0x91f555(0xd1)][_0x91f555(0x1ac)]>_0x13b741['global'][_0x91f555(0x11f)]||_0x470e69['hits'][_0x91f555(0x145)]>_0x13b741[_0x91f555(0x1c3)][_0x91f555(0x138)])&&(_0x470e69['hits'][_0x91f555(0x105)]=!0x0);}}catch{}}}return _0x853c2d;}function G(_0x7dac70){var _0x4fb4c4=_0x34ea16;if(_0x7dac70&&typeof _0x7dac70=='object'&&_0x7dac70[_0x4fb4c4(0x118)])switch(_0x7dac70[_0x4fb4c4(0x118)][_0x4fb4c4(0xc8)]){case _0x4fb4c4(0x14a):return _0x7dac70['hasOwnProperty'](Symbol[_0x4fb4c4(0x19c)])?Promise[_0x4fb4c4(0xfc)]():_0x7dac70;case _0x4fb4c4(0x160):return Promise[_0x4fb4c4(0xfc)]();}return _0x7dac70;}((_0x749b89,_0x2827b8,_0x580b99,_0x418fc9,_0x44c2a9,_0x23ed43,_0x1b9ef6,_0x24f371,_0x5d1b5f,_0x70341a,_0x561d12,_0x5c054b)=>{var _0x25b334=_0x34ea16;if(_0x749b89[_0x25b334(0x1a4)])return _0x749b89[_0x25b334(0x1a4)];let _0x294980={'consoleLog':()=>{},'consoleTrace':()=>{},'consoleTime':()=>{},'consoleTimeEnd':()=>{},'autoLog':()=>{},'autoLogMany':()=>{},'autoTraceMany':()=>{},'coverage':()=>{},'autoTrace':()=>{},'autoTime':()=>{},'autoTimeEnd':()=>{}};if(!X(_0x749b89,_0x24f371,_0x44c2a9))return _0x749b89[_0x25b334(0x1a4)]=_0x294980,_0x749b89[_0x25b334(0x1a4)];let _0x4ac171=b(_0x749b89),_0x44ae90=_0x4ac171['elapsed'],_0x281c3c=_0x4ac171['timeStamp'],_0x156600=_0x4ac171['now'],_0x564ce7={'hits':{},'ts':{}},_0x20d77e=J(_0x749b89,_0x5d1b5f,_0x564ce7,_0x23ed43,_0x5c054b,_0x44c2a9===_0x25b334(0x114)?G:void 0x0),_0x1583f6=(_0x138848,_0x4e327a,_0x410ab7,_0x1afae4,_0xf58f6a,_0x49dcce)=>{var _0x41f612=_0x25b334;let _0x224524=_0x749b89[_0x41f612(0x1a4)];try{return _0x749b89[_0x41f612(0x1a4)]=_0x294980,_0x20d77e(_0x138848,_0x4e327a,_0x410ab7,_0x1afae4,_0xf58f6a,_0x49dcce);}finally{_0x749b89[_0x41f612(0x1a4)]=_0x224524;}},_0x3178b9=_0x202de7=>{_0x564ce7['ts'][_0x202de7]=_0x281c3c();},_0x13cc1b=(_0x1c13ca,_0x42a326)=>{var _0x3ed0b6=_0x25b334;let _0x4c546f=_0x564ce7['ts'][_0x42a326];if(delete _0x564ce7['ts'][_0x42a326],_0x4c546f){let _0x4f11ca=_0x44ae90(_0x4c546f,_0x281c3c());_0xe67207(_0x1583f6(_0x3ed0b6(0x145),_0x1c13ca,_0x156600(),_0x468b04,[_0x4f11ca],_0x42a326));}},_0x53c802=_0x4eb69a=>{var _0x3b77b5=_0x25b334,_0x408cd3;return _0x44c2a9===_0x3b77b5(0x114)&&_0x749b89[_0x3b77b5(0x188)]&&((_0x408cd3=_0x4eb69a==null?void 0x0:_0x4eb69a[_0x3b77b5(0x129)])==null?void 0x0:_0x408cd3['length'])&&(_0x4eb69a['args'][0x0][_0x3b77b5(0x188)]=_0x749b89[_0x3b77b5(0x188)]),_0x4eb69a;};_0x749b89[_0x25b334(0x1a4)]={'consoleLog':(_0x27f701,_0x2ce433)=>{var _0x1a8f8a=_0x25b334;_0x749b89['console']['log'][_0x1a8f8a(0xc8)]!=='disabledLog'&&_0xe67207(_0x1583f6(_0x1a8f8a(0x172),_0x27f701,_0x156600(),_0x468b04,_0x2ce433));},'consoleTrace':(_0x9d0f76,_0x963e49)=>{var _0xbd38e=_0x25b334,_0x291f9e,_0x22bd5e;_0x749b89[_0xbd38e(0xea)][_0xbd38e(0x172)]['name']!==_0xbd38e(0x161)&&((_0x22bd5e=(_0x291f9e=_0x749b89['process'])==null?void 0x0:_0x291f9e['versions'])!=null&&_0x22bd5e['node']&&(_0x749b89[_0xbd38e(0x11a)]=!0x0),_0xe67207(_0x53c802(_0x1583f6('trace',_0x9d0f76,_0x156600(),_0x468b04,_0x963e49))));},'consoleError':(_0x12a0af,_0x39f06f)=>{var _0x3ec0a4=_0x25b334;_0x749b89[_0x3ec0a4(0x11a)]=!0x0,_0xe67207(_0x53c802(_0x1583f6('error',_0x12a0af,_0x156600(),_0x468b04,_0x39f06f)));},'consoleTime':_0x3f0828=>{_0x3178b9(_0x3f0828);},'consoleTimeEnd':(_0x1a4f7e,_0x345701)=>{_0x13cc1b(_0x345701,_0x1a4f7e);},'autoLog':(_0x52f6fa,_0x1cedc2)=>{var _0x418819=_0x25b334;_0xe67207(_0x1583f6(_0x418819(0x172),_0x1cedc2,_0x156600(),_0x468b04,[_0x52f6fa]));},'autoLogMany':(_0x6057b7,_0x1b7e26)=>{var _0x49b88c=_0x25b334;_0xe67207(_0x1583f6(_0x49b88c(0x172),_0x6057b7,_0x156600(),_0x468b04,_0x1b7e26));},'autoTrace':(_0x4b61cf,_0x2766f3)=>{_0xe67207(_0x53c802(_0x1583f6('trace',_0x2766f3,_0x156600(),_0x468b04,[_0x4b61cf])));},'autoTraceMany':(_0x3e5181,_0x16f50e)=>{var _0x286dd8=_0x25b334;_0xe67207(_0x53c802(_0x1583f6(_0x286dd8(0x182),_0x3e5181,_0x156600(),_0x468b04,_0x16f50e)));},'autoTime':(_0x2c4b10,_0x4be44a,_0x2ba611)=>{_0x3178b9(_0x2ba611);},'autoTimeEnd':(_0x5e03ff,_0x5980db,_0x317c23)=>{_0x13cc1b(_0x5980db,_0x317c23);},'coverage':_0x5a8518=>{var _0x3f417c=_0x25b334;_0xe67207({'method':_0x3f417c(0x1c1),'version':_0x23ed43,'args':[{'id':_0x5a8518}]});}};let _0xe67207=H(_0x749b89,_0x2827b8,_0x580b99,_0x418fc9,_0x44c2a9,_0x70341a,_0x561d12),_0x468b04=_0x749b89['_console_ninja_session'];return _0x749b89['_console_ninja'];})(globalThis,_0x34ea16(0xe7),_0x34ea16(0x12c),_0x34ea16(0x133),_0x34ea16(0xe5),'1.0.0',_0x34ea16(0x18e),_0x34ea16(0x1ba),_0x34ea16(0x17f),_0x34ea16(0xd6),'1',{\"resolveGetters\":false,\"defaultLimits\":{\"props\":100,\"elements\":100,\"strLength\":51200,\"totalStrLength\":51200,\"autoExpandLimit\":5000,\"autoExpandMaxDepth\":10},\"reducedLimits\":{\"props\":5,\"elements\":5,\"strLength\":256,\"totalStrLength\":768,\"autoExpandLimit\":30,\"autoExpandMaxDepth\":2},\"reducePolicy\":{\"perLogpoint\":{\"reduceOnCount\":50,\"reduceOnAccumulatedProcessingTimeMs\":100,\"resetWhenQuietMs\":500,\"resetOnProcessingTimeAverageMs\":100},\"global\":{\"reduceOnCount\":1000,\"reduceOnAccumulatedProcessingTimeMs\":300,\"resetWhenQuietMs\":50,\"resetOnProcessingTimeAverageMs\":100}}});");}catch(e){}};/* istanbul ignore next */function oo_oo(/**@type{any}**/i,/**@type{any}**/...v){try{oo_cm().consoleLog(i, v);}catch(e){} return v};/* istanbul ignore next */function oo_tr(/**@type{any}**/i,/**@type{any}**/...v){try{oo_cm().consoleTrace(i, v);}catch(e){} return v};/* istanbul ignore next */function oo_tx(/**@type{any}**/i,/**@type{any}**/...v){try{oo_cm().consoleError(i, v);}catch(e){} return v};/* istanbul ignore next */function oo_ts(/**@type{any}**/v){try{oo_cm().consoleTime(v);}catch(e){} return v;};/* istanbul ignore next */function oo_te(/**@type{any}**/v, /**@type{any}**/i){try{oo_cm().consoleTimeEnd(v, i);}catch(e){} return v;};/*eslint unicorn/no-abusive-eslint-disable:,eslint-comments/disable-enable-pair:,eslint-comments/no-unlimited-disable:,eslint-comments/no-aggregating-enable:,eslint-comments/no-duplicate-disable:,eslint-comments/no-unused-disable:,eslint-comments/no-unused-enable:,*/